import React, { useState, useEffect, useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQueryClient, useQuery } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'
import { api } from '@/api/client'
import { ConfirmDialog } from '@/shared/components/ui/ConfirmDialog'
import { MeetingConflictNotice, type MeetingConflict } from '@/shared/components/ui/MeetingConflictNotice'
import { getApiErrorMessage } from '@/shared/utils/apiError'
import { useAuth } from '@/context/AuthContext'
import { isSuperAdminOrOwner } from '@jira-clone/shared-types'
import { useCancelDeletionRequest, useDeleteCrmRecord, usePendingDeletionRequest, isDatabaseId } from '@/shared/hooks/useCrmDeletion'
import { useLeadSources, useLeadStatuses, useDealStages } from '@/shared/hooks/useCrmVocabulary'
import { LeadRecordingsPanel } from './lead-recordings-panel'
import { useLeadMeetings, LeadMeeting, CreateLeadMeetingInput, UpdateLeadMeetingInput } from '@/shared/hooks/useLeadMeetings'
import { useLeadTodos } from '@/shared/hooks/useTodos'
import { useLeadComments } from '@/shared/hooks/useComments'
import { useRefreshWidgets } from '@/features/dashboard/api'
import { formatDistanceToNow } from 'date-fns'
import { formatIndianDateTime, toIndianIsoString, toIndianDatetimeLocal } from '@/shared/utils/dateTime'
import {
  useLeadCallLogs,
  useLeadFollowups,
  useLeadCallHistory,
  LeadCallLog,
  LeadFollowup,
  LeadCallHistoryItem,
  CreateLeadCallLogInput,
  CreateLeadFollowupInput,
  CreateLeadCallHistoryInput,
} from '@/shared/hooks/useLeadActivity'
import { useProductServices, ProductService } from '@/shared/hooks/useProductServices'
import { ServicesPicker } from './ServicesPicker'
import { useCallScript } from '@/features/leads/CallScriptContext'
import { INDUSTRY_OPTIONS, getScriptByIndustry } from '@/features/leads/callScripts'
import { CallScriptPanel } from '@/features/leads/CallScriptPanel'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Button,
  Input,
  Textarea,
  Badge,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from '@goodday-clone/ui'
import {
  Droplet,
  Plus,
  Copy,
  Zap,
  Settings,
  MoreHorizontal,
  Bell,
  Pin,
  Maximize2,
  Minimize2,
  X,
  CheckCircle2,
  Clock,
  Calendar,
  Flag,
  Mail,
  Phone,
  Layers,
  Flame,
  Snowflake,
  FileText,
  Building,
  User,
  CheckSquare,
  Square,
  Send,
  Eye,
  ChevronDown,
  Sparkles,
  Star,
  ListTodo,
  GitMerge,
  ExternalLink,
  Activity,
  HeartPulse,
  Repeat,
  Play,
  Pencil,
  FileDown,
  Trash2,
  Archive,
  AlertTriangle,
  Ban,
  ChevronsUp,
  ChevronUp,
  Circle,
  ChevronsDown,
  Link as LinkIcon,
  BadgeDollarSign,
  Kanban,
  ArrowRight,
  Check,
  Lock,
  EyeOff,
  Volume2,
  VolumeX,
  ArrowDownUp,
  PhoneCall,
  PhoneIncoming,
  PhoneOutgoing,
  CalendarDays,
  CalendarPlus,
  History,
  Video,
  MapPin,
  Mic,
  MessageSquare,
  BookOpen,
} from 'lucide-react'

const PRIORITY_OPTIONS = [
  { label: 'Emergency', value: 'Emergency', icon: AlertTriangle, color: 'text-red-500', bg: 'bg-red-500/10 text-red-600 dark:text-red-400' },
  { label: 'Blocker', value: 'Blocker', icon: Ban, color: 'text-red-500', bg: 'bg-red-500/10 text-red-600 dark:text-red-400' },
  { label: 'Highest', value: 'Highest', icon: ChevronsUp, color: 'text-orange-500', bg: 'bg-orange-500/10 text-orange-600 dark:text-orange-400' },
  { label: 'High', value: 'High', icon: ChevronUp, color: 'text-orange-400', bg: 'bg-orange-400/10 text-orange-600 dark:text-orange-400' },
  { label: 'Normal', value: 'Normal', icon: Circle, color: 'text-slate-400', bg: 'bg-slate-500/10 text-slate-600 dark:text-slate-300' },
  { label: 'Low', value: 'Low', icon: ChevronDown, color: 'text-blue-400', bg: 'bg-blue-400/10 text-blue-600 dark:text-blue-400' },
  { label: 'Lowest', value: 'Lowest', icon: ChevronsDown, color: 'text-blue-500', bg: 'bg-blue-500/10 text-blue-600 dark:text-blue-400' },
] as const

export interface LeadDetailData {
  id: string
  name: string
  role?: string
  company?: string
  contactPerson?: string
  email?: string
  phone?: string
  website?: string
  address?: string
  mapsUrl?: string
  assignee?: string
  industry?: string
  actionRequired?: string
  actionRequiredUserId?: string
  source?: string
  status?: string
  value?: number | string
  interest?: string
  rating?: number | null
  notes?: string
  services?: string[]
  number?: string | number
  createdAt?: string
  organizationId?: string
  convertedDealId?: string
  isRecurring?: boolean
  recurringFrequency?: string
  nextRecurringDate?: string
  reminders?: any[]
  pinned?: boolean
}

interface LeadDetailModalProps {
  isOpen: boolean
  onClose: () => void
  lead: LeadDetailData | null
  onUpdate?: (updatedLead: LeadDetailData) => void
  onDeleted?: (id: string) => void
  currentSectionName?: string
  currentSectionHref?: string
}

const API_BASE_URL = (typeof process !== 'undefined' && process.env?.NEXT_PUBLIC_API_URL) || (import.meta as any)?.env?.VITE_API_URL || 'http://localhost:4000'

const INTEREST_OPTIONS = [
  { label: 'Warm', value: 'Warm', color: 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300' },
  { label: 'Hot', value: 'Hot', color: 'bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300' },
  { label: 'Cold', value: 'Cold', color: 'bg-sky-100 text-sky-800 dark:bg-sky-950/60 dark:text-sky-300' },
]

export function parseScraperRatingAndReviews(notes?: string | null, directRating?: number | null) {
  let rating = typeof directRating === 'number' && !isNaN(directRating) ? directRating : null
  let reviewCount: string | null = null
  let isFromScraper = false

  if (notes) {
    if (notes.includes('Scraped from Google Maps') || notes.includes('Google Maps:')) {
      isFromScraper = true
    }
    const match = notes.match(/Rating:\s*([\d.]+)(?:\s*\(([^)]+)\))?/i)
    if (match) {
      if (rating === null) {
        const parsed = parseFloat(match[1])
        if (!isNaN(parsed) && parsed > 0 && parsed <= 5) {
          rating = parsed
        }
      }
      if (match[2]) {
        reviewCount = match[2].trim()
      }
    }
  }

  return { rating, reviewCount, isFromScraper }
}

const COORD = '-?\\d+(?:\\.\\d+)?'

// notes is free-text and user-editable, so a scraped link is only ever
// followed after confirming it is a real http(s) URL.
function asSafeExternalUrl(value?: string | null) {
  if (!value) return null
  try {
    const url = new URL(value)
    return url.protocol === 'https:' || url.protocol === 'http:' ? value : null
  } catch {
    return null
  }
}

export function parseScraperMapInfo(
  notes?: string | null,
  address?: string | null,
  savedMapsUrl?: string | null,
) {
  const mapsUrl =
    asSafeExternalUrl(notes?.match(/Scraped from Google Maps:\s*(\S+)/i)?.[1]) ||
    asSafeExternalUrl(savedMapsUrl)

  let lat: number | null = null
  let lng: number | null = null
  if (mapsUrl) {
    // Google place URLs carry the pinned coordinate as "!3d<lat>!4d<lng>";
    // @lat,lng and ?q=lat,lng cover the shared/bookmark URL variants.
    const patterns = [
      new RegExp(`!3d(${COORD})!4d(${COORD})`),
      new RegExp(`@(${COORD}),(${COORD})`),
      new RegExp(`[?&]q=(${COORD}),(${COORD})`),
    ]
    for (const pattern of patterns) {
      const match = mapsUrl.match(pattern)
      if (match) {
        const parsedLat = parseFloat(match[1])
        const parsedLng = parseFloat(match[2])
        if (Math.abs(parsedLat) <= 90 && Math.abs(parsedLng) <= 180) {
          lat = parsedLat
          lng = parsedLng
          break
        }
      }
    }
  }

  // The embedded map pins on coordinates when the link carries them, otherwise on the
  // address, and failing that on the place named in the link itself.
  const placeFromUrl = (() => {
    const segment = mapsUrl?.match(/\/maps\/(?:place|search)\/([^?]+)/)?.[1]
    if (!segment) return ''
    return decodeURIComponent(segment.split('/data=')[0].replace(/\+/g, ' ')).trim()
  })()

  const query =
    lat !== null && lng !== null
      ? `${lat},${lng}`
      : address?.trim() || placeFromUrl || ''
  const embedSrc = query
    ? `https://maps.google.com/maps?q=${encodeURIComponent(query)}&z=${lat !== null ? 16 : 15}&output=embed`
    : null

  return { mapsUrl, lat, lng, embedSrc }
}

export function LeadDetailModal({
  isOpen,
  onClose,
  lead,
  onUpdate,
  onDeleted,
  currentSectionName = 'All Leads',
  currentSectionHref = '/app/leads',
}: LeadDetailModalProps) {
  const { data: leadStatuses = [] } = useLeadStatuses()
  const { data: dealStages = [] } = useDealStages()
  const { data: leadSources = [] } = useLeadSources()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { data: masterServices = [] } = useProductServices({ isActive: true })

  // Local Editable States
  const [name, setName] = useState(lead?.name || '')
  const [company, setCompany] = useState(lead?.company || lead?.name || '')
  const [contactPerson, setContactPerson] = useState(lead?.contactPerson || lead?.name || '')
  const [email, setEmail] = useState(lead?.email || '')
  const [phone, setPhone] = useState(lead?.phone || '')
  const [website, setWebsite] = useState(lead?.website || '')
  const [address, setAddress] = useState(lead?.address || '')
  const [source, setSource] = useState(lead?.source || '')
  const [status, setStatus] = useState(lead?.status || 'New')
  const [interest, setInterest] = useState(lead?.interest || 'Warm')
  const [rating, setRating] = useState<number | null>(() => {
    const parsed = parseScraperRatingAndReviews((lead as any)?.notes, (lead as any)?.rating)
    return parsed.rating ?? null
  })
  const [hoverRating, setHoverRating] = useState<number | null>(null)
  const [assignee, setAssignee] = useState(lead?.assignee || '')
  const [actionRequired, setActionRequired] = useState(lead?.actionRequired || '')
  const [actionRequiredUserId, setActionRequiredUserId] = useState(lead?.actionRequiredUserId || '')
  const [notes, setNotes] = useState(lead?.notes || '')
  const [description, setDescription] = useState(lead?.notes || '')
  const [mapsUrl, setMapsUrl] = useState(lead?.mapsUrl || '')
  const [isFetchingMaps, setIsFetchingMaps] = useState(false)
  const [mapsLinkDraft, setMapsLinkDraft] = useState('')
  function stripCurrencyPrefix(v: any): string {
    if (v === undefined || v === null) return ''
    if (typeof v === 'number') return v.toLocaleString('en-IN')
    return String(v).replace(/^[₹$]\s*/, '').trim()
  }

  const [value, setValue] = useState(stripCurrencyPrefix(lead?.value))
  const [services, setServices] = useState<string[]>(lead?.services || [])

  const mapInfo = useMemo(
    () => parseScraperMapInfo(description || notes, address, mapsUrl),
    [description, notes, address, mapsUrl],
  )

  // Master-data service lookup and total computation
  const masterDataByName = useMemo(() => {
    const map: Record<string, ProductService> = {}
    for (const ps of masterServices) {
      if (ps.name) map[ps.name.toLowerCase()] = ps
    }
    return map
  }, [masterServices])

  const totalFromServices = useMemo(() => {
    if (services.length === 0) return 0
    return services.reduce((sum, svc) => {
      const md = masterDataByName[svc.toLowerCase()]
      return sum + (md?.defaultRate || 0)
    }, 0)
  }, [services, masterDataByName])

  const formatCurrency = (amount: number) => {
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(amount % 100000 === 0 ? 0 : 1)}L`
    if (amount > 0) return `₹${amount.toLocaleString('en-IN')}`
    return ''
  }

  // Call Script State
  const [selectedIndustry, setSelectedIndustry] = useState<string>((lead as any)?.industry || '')
  const { openScript } = useCallScript()

  // Priority & Custom Fields State
  const [priority, setPriority] = useState<string>((lead as any)?.priority || 'Normal')
  const [leadData, setLeadData] = useState<LeadDetailData | null>(lead)
  const [pinned, setPinned] = useState(Boolean(lead?.pinned))
  const [customLinks, setCustomLinks] = useState<{ id: string; url: string; label: string }[]>([])
  const [isAddingLink, setIsAddingLink] = useState(false)
  const [newLinkUrl, setNewLinkUrl] = useState('https://')
  const [newLinkLabel, setNewLinkLabel] = useState('')
  const [showAllFields, setShowAllFields] = useState(false)

  // Move to Deal State
  const [isMoveToDealOpen, setIsMoveToDealOpen] = useState(false)
  const [targetDealStage, setTargetDealStage] = useState('Discovery')
  const [targetDealTitle, setTargetDealTitle] = useState('')
  const [targetDealValue, setTargetDealValue] = useState('')
  const [isConvertingDeal, setIsConvertingDeal] = useState(false)

  // Settings & Form Preferences
  const [visibleSections, setVisibleSections] = useState({
    todos: true,
    dependencies: true,
    customFields: true,
    crm: true,
    callLogs: true,
    followups: true,
    callHistory: true,
    recordings: true,
    appointments: true,
  })
  const [commentSortOrder, setCommentSortOrder] = useState<'newest' | 'oldest'>('oldest')
  const [watchStatus, setWatchStatus] = useState<'watching' | 'normal' | 'muted'>('watching')

  // Top View Mode: 'all' (Overview) or dedicated feature tabs
  const [activeTab, setActiveTab] = useState<'all' | 'calls' | 'followups' | 'callHistory' | 'appointments'>('all')

  // 1. Call Logs State - backed by useLeadCallLogs, a real API-persisted
  // resource with a real user behind the rep, not a free-text name.
  const { callLogs, createCallLog, updateCallLog, deleteCallLog } = useLeadCallLogs(lead?.id)
  const [isAddingCallLog, setIsAddingCallLog] = useState(false)
  const [isSavingCallLog, setIsSavingCallLog] = useState(false)
  const [editingCallLogId, setEditingCallLogId] = useState<string | null>(null)
  const [isSavingCallLogEdit, setIsSavingCallLogEdit] = useState(false)
  const [newCallType, setNewCallType] = useState<'Outbound' | 'Inbound'>('Outbound')
  const [newCallOutcome, setNewCallOutcome] = useState<CreateLeadCallLogInput['outcome']>('Connected')
  const [newCallDurationMinutes, setNewCallDurationMinutes] = useState(15)
  const [newCallRepId, setNewCallRepId] = useState('')
  const [newCallNotes, setNewCallNotes] = useState('')
  const [newCallSentiment, setNewCallSentiment] = useState<'Positive' | 'Neutral' | 'Hesitant'>('Positive')

  // 2. Followups State - backed by useLeadFollowups, a real API-persisted resource.
  const { followups, createFollowup, updateFollowup, toggleFollowup, deleteFollowup } = useLeadFollowups(lead?.id)
  const [isAddingFollowup, setIsAddingFollowup] = useState(false)
  const [isSavingFollowup, setIsSavingFollowup] = useState(false)
  const [editingFollowupId, setEditingFollowupId] = useState<string | null>(null)
  const [isSavingFollowupEdit, setIsSavingFollowupEdit] = useState(false)
  const [followupFilter, setFollowupFilter] = useState<'all' | 'pending' | 'completed'>('all')
  const [newFollowupTitle, setNewFollowupTitle] = useState('')
  const [newFollowupType, setNewFollowupType] = useState<NonNullable<CreateLeadFollowupInput['type']>>('Call')
  const [newFollowupDueAt, setNewFollowupDueAt] = useState('')
  const [newFollowupRepId, setNewFollowupRepId] = useState('')
  const [newFollowupPriority, setNewFollowupPriority] = useState<NonNullable<CreateLeadFollowupInput['priority']>>('High')
  const [newFollowupNotes, setNewFollowupNotes] = useState('')

  // 3. Call History State - backed by useLeadCallHistory, a real API-persisted resource.
  const { callHistory, createCallHistory, updateCallHistory, deleteCallHistory } = useLeadCallHistory(lead?.id)
  const [isAddingHistory, setIsAddingHistory] = useState(false)
  const [isSavingHistory, setIsSavingHistory] = useState(false)
  const [editingHistoryId, setEditingHistoryId] = useState<string | null>(null)
  const [isSavingHistoryEdit, setIsSavingHistoryEdit] = useState(false)
  const [newHistTitle, setNewHistTitle] = useState('')
  const [newHistRepId, setNewHistRepId] = useState('')
  const [newHistOutcome, setNewHistOutcome] = useState('Qualified & Progressed')
  const [newHistSentiment, setNewHistSentiment] = useState<NonNullable<CreateLeadCallHistoryInput['sentiment']>>('Positive')
  const [newHistDurationMinutes, setNewHistDurationMinutes] = useState(20)
  const [newHistSummary, setNewHistSummary] = useState('')
  const [newHistKeyPoint, setNewHistKeyPoint] = useState('')
  const [newHistNextAction, setNewHistNextAction] = useState('')

  // 4. Appointments (Meetings) State - backed by useLeadMeetings, a real
  // API-persisted resource with real invited users, not localStorage.
  const { meetings: appointments, createMeeting, updateMeeting, deleteMeeting: deleteMeetingApi } = useLeadMeetings(lead?.id)
  // Meetings booked here also appear on the dashboard's calendar and
  // reminders cards, so every add/edit/delete below must invalidate those.
  const refreshMeetingWidgets = useRefreshWidgets()
  // The earliest meeting still ahead of us (not cancelled or completed) is the lead's "Schedule".
  const nextMeeting = useMemo(() => {
    const now = Date.now()
    return (
      [...appointments]
        .filter((a) => a.status !== 'Cancelled' && a.status !== 'Completed' && new Date(a.scheduledAt).getTime() >= now)
        .sort((a, b) => new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime())[0] ?? null
    )
  }, [appointments])
  const [isAddingAppt, setIsAddingAppt] = useState(false)
  const [isSavingAppt, setIsSavingAppt] = useState(false)
  const [editingApptId, setEditingApptId] = useState<string | null>(null)
  const [isSavingApptEdit, setIsSavingApptEdit] = useState(false)
  const [newApptTitle, setNewApptTitle] = useState('')
  const [newApptScheduledAt, setNewApptScheduledAt] = useState('')
  const [newApptDurationMinutes, setNewApptDurationMinutes] = useState(30)
  const [newApptType, setNewApptType] = useState<CreateLeadMeetingInput['type']>('Google Meet')
  const [newApptHostId, setNewApptHostId] = useState('')
  const [newApptAttendeeIds, setNewApptAttendeeIds] = useState<string[]>([])
  const [newApptUrl, setNewApptUrl] = useState('')
  const [newApptAgenda, setNewApptAgenda] = useState('')
  const [newApptStatus, setNewApptStatus] = useState<LeadMeeting['status']>('Confirmed')
  // Sales department + Super Admin: lead owner, action owner, meeting host and reps.
  const [assignableUsers, setAssignableUsers] = useState<{ id: string; name: string; username?: string; email?: string }[]>([])
  // Anyone can be invited to a meeting as an attendee.
  const [allUsers, setAllUsers] = useState<{ id: string; name: string; email?: string; role?: string }[]>([])

  // Live double-booking warning for the appointment forms: checks the
  // proposed window against the host's + every invited attendee's bookings
  // (e.g. tells you the Super Admin is already in another meeting then).
  const apptFormOpen = isAddingAppt || Boolean(editingApptId)
  const { data: apptConflicts = [] } = useQuery<MeetingConflict[]>({
    queryKey: [
      'meeting-conflicts',
      lead?.id,
      newApptScheduledAt,
      newApptDurationMinutes,
      newApptHostId,
      newApptAttendeeIds.join(','),
      editingApptId || 'new',
    ],
    queryFn: async () => {
      const res = await api.get<any>(
        '/meetings/conflicts',
        {
          scheduledAt: toIndianIsoString(newApptScheduledAt),
          durationMinutes: newApptDurationMinutes,
          participantIds: [newApptHostId, ...newApptAttendeeIds].filter(Boolean).join(','),
          excludeMeetingId: editingApptId || undefined,
        },
        { skipErrorToast: true }
      )
      return res?.data || []
    },
    enabled: Boolean(lead?.id) && apptFormOpen && Boolean(newApptScheduledAt) && Boolean(newApptHostId),
    staleTime: 30_000,
  })

  // Pre-fetch assignable users so the lead's call-log, followup, call-history
  // and appointment forms can default to the lead's assigned rep / host.
  useEffect(() => {
    api
      .get<{ data: { id: string; name: string; email?: string }[] }>('/users/assignable?for=leads')
      .then((res) => setAssignableUsers(res?.data || []))
      .catch(() => {})
    api
      .get<{ data: { id: string; name: string; email?: string; role?: string }[] }>('/users/assignable')
      .then((res) => setAllUsers(res?.data || []))
      .catch(() => {})
  }, [])

  // Once both the lead's assignee and the assignable users are known, default
  // the rep/host selections to the assigned person. Runs again if the lead
  // is reassigned so the forms stay in sync. Falls back to the first user
  // only when no assignee is set or it can't be matched.
  useEffect(() => {
    if (assignableUsers.length === 0 || !lead?.assignee) return
    const assignedName = lead.assignee
    const match = assignableUsers.find((u) => u.name.toLowerCase() === assignedName.toLowerCase())
    const defaultUser = match || assignableUsers[0]
    if (!defaultUser) return
    setNewApptHostId(defaultUser.id)
    setNewCallRepId(defaultUser.id)
    setNewFollowupRepId(defaultUser.id)
    setNewHistRepId(defaultUser.id)
  }, [assignee, assignableUsers])

  // Todos - real backend persistence via GoodDayTodo (see useTodos.ts)
  const { todos, createTodo, toggleTodo, deleteTodo } = useLeadTodos(lead?.id)
  const [newTodoText, setNewTodoText] = useState('')
  const [isAddingTodo, setIsAddingTodo] = useState(false)

  // Activity / Comments - real backend persistence via the generic
  // GoodDayComment system (see useComments.ts)
  const { comments, createComment, refetch: refetchComments } = useLeadComments(lead?.id)
  const [replyText, setReplyText] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [saveMessage, setSaveMessage] = useState<string | null>(null)
  const [isDeleteOpen, setIsDeleteOpen] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [deleteReason, setDeleteReason] = useState('')
  const { user: currentUser } = useAuth()
  const mayDeleteAlone = isSuperAdminOrOwner(currentUser?.role)
  const deleteRecord = useDeleteCrmRecord()
  const cancelDeleteRequest = useCancelDeletionRequest()
  // Demo rows (local `lead-*` ids) have no database record to ask about.
  const { pending: pendingDelete } = usePendingDeletionRequest(
    'lead',
    isDatabaseId(lead?.id) ? lead?.id : undefined
  )
  const [isFullscreen, setIsFullscreen] = useState(false)

  // Sync state when lead prop changes
  useEffect(() => {
    if (lead) {
      setLeadData(lead)
      setPinned(Boolean(lead.pinned))
      setName(lead.name || '')
      setCompany(lead.company || lead.name || '')
      setContactPerson(lead.contactPerson || lead.name || '')
      setEmail(lead.email || '')
      setPhone(lead.phone || '')
      setWebsite(lead.website || '')
      setMapsUrl(lead.mapsUrl || '')
      setAddress(lead.address || '')
      setSource(lead.source || '')
      setStatus(lead.status || 'New')
      setInterest(lead.interest || 'Warm')
      const parsedScraped = parseScraperRatingAndReviews(lead.notes, (lead as any).rating)
      setRating(parsedScraped.rating)
      if ((lead as any).rating == null && parsedScraped.rating !== null) {
        persistChanges({ rating: parsedScraped.rating })
      }
      setAssignee(lead.assignee || '')
      setActionRequired(lead.actionRequired || '')
      setActionRequiredUserId(lead.actionRequiredUserId || '')
      setNotes(lead.notes || '')
      setDescription(lead.notes || '')
      setValue(stripCurrencyPrefix(lead.value))
      setServices(lead.services || [])
      setSelectedIndustry((lead as any)?.industry || '')
      setIsRecurring(Boolean(lead.isRecurring))
      setRecurringFrequency(lead.recurringFrequency || 'Weekly')

      setEditingCallLogId(null)
      setEditingFollowupId(null)
      setEditingHistoryId(null)
      setEditingApptId(null)
      setIsAddingCallLog(false)
      setIsAddingFollowup(false)
      setIsAddingHistory(false)
      setIsAddingAppt(false)
    }
  }, [lead])

  // Call Log Actions - persisted via useLeadCallLogs, not localStorage.
  const handleSaveCallLog = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newCallNotes.trim() || !newCallRepId) return
    setIsSavingCallLog(true)
    try {
      await createCallLog({
        type: newCallType,
        outcome: newCallOutcome,
        durationMinutes: newCallDurationMinutes,
        repUserId: newCallRepId,
        notes: newCallNotes.trim(),
        sentiment: newCallSentiment,
      })
      setNewCallNotes('')
      setIsAddingCallLog(false)
      setSaveMessage('Call logged successfully!')
      setTimeout(() => setSaveMessage(null), 2500)
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'log this call'))
    } finally {
      setIsSavingCallLog(false)
    }
  }

  const handleDeleteCallLog = async (id: string) => {
    try {
      await deleteCallLog(id)
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'delete this call log'))
    }
  }

  const handleStartEditCallLog = (log: any) => {
    setEditingCallLogId(log.id)
    setNewCallType(log.type)
    setNewCallOutcome(log.outcome)
    setNewCallDurationMinutes(log.durationMinutes)
    setNewCallRepId(log.rep?.id || '')
    setNewCallNotes(log.notes)
    setNewCallSentiment(log.sentiment || 'Neutral')
    setIsAddingCallLog(false)
  }

  const handleCancelEditCallLog = () => {
    setEditingCallLogId(null)
    setNewCallNotes('')
    setNewCallRepId('')
  }

  const handleUpdateCallLog = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingCallLogId || !newCallNotes.trim() || !newCallRepId) return
    setIsSavingCallLogEdit(true)
    try {
      await updateCallLog(editingCallLogId, {
        type: newCallType,
        outcome: newCallOutcome,
        durationMinutes: newCallDurationMinutes,
        repUserId: newCallRepId,
        notes: newCallNotes.trim(),
        sentiment: newCallSentiment,
      })
      setEditingCallLogId(null)
      setSaveMessage('Call log updated!')
      setTimeout(() => setSaveMessage(null), 2500)
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'update this call log'))
    } finally {
      setIsSavingCallLogEdit(false)
    }
  }

  // Followup Actions - persisted via useLeadFollowups, not localStorage.
  const handleSaveFollowup = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newFollowupTitle.trim() || !newFollowupDueAt || !newFollowupRepId) return
    setIsSavingFollowup(true)
    try {
      await createFollowup({
        title: newFollowupTitle.trim(),
        type: newFollowupType,
        dueAt: toIndianIsoString(newFollowupDueAt),
        repUserId: newFollowupRepId,
        priority: newFollowupPriority,
        notes: newFollowupNotes.trim() || undefined,
      })
      setNewFollowupTitle('')
      setNewFollowupNotes('')
      setIsAddingFollowup(false)
      setSaveMessage('Follow-up scheduled!')
      setTimeout(() => setSaveMessage(null), 2500)
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'schedule this follow-up'))
    } finally {
      setIsSavingFollowup(false)
    }
  }

  const handleToggleFollowup = async (id: string) => {
    const current = followups.find((f) => f.id === id)
    if (!current) return
    try {
      await toggleFollowup(id, current.status)
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'update this follow-up'))
    }
  }

  const handleStartEditFollowup = (flw: LeadFollowup) => {
    setEditingFollowupId(flw.id)
    setNewFollowupTitle(flw.title)
    setNewFollowupType(flw.type)
    try {
      setNewFollowupDueAt(toIndianDatetimeLocal(flw.dueAt))
    } catch {
      setNewFollowupDueAt('')
    }
    setNewFollowupRepId(flw.rep?.id || '')
    setNewFollowupPriority(flw.priority)
    setNewFollowupNotes(flw.notes || '')
    setIsAddingFollowup(false)
  }

  const handleCancelEditFollowup = () => {
    setEditingFollowupId(null)
    setNewFollowupTitle('')
    setNewFollowupNotes('')
  }

  const handleUpdateFollowup = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingFollowupId || !newFollowupTitle.trim() || !newFollowupDueAt || !newFollowupRepId) return
    setIsSavingFollowupEdit(true)
    try {
      await updateFollowup(editingFollowupId, {
        title: newFollowupTitle.trim(),
        type: newFollowupType,
        dueAt: toIndianIsoString(newFollowupDueAt),
        repUserId: newFollowupRepId,
        priority: newFollowupPriority,
        notes: newFollowupNotes.trim() || undefined,
      })
      setEditingFollowupId(null)
      setSaveMessage('Follow-up updated!')
      setTimeout(() => setSaveMessage(null), 2500)
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'update this follow-up'))
    } finally {
      setIsSavingFollowupEdit(false)
    }
  }

  const handleDeleteFollowup = async (id: string) => {
    try {
      await deleteFollowup(id)
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'delete this follow-up'))
    }
  }

  // Call History Actions - persisted via useLeadCallHistory, not localStorage.
  const handleSaveHistory = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newHistTitle.trim() || !newHistSummary.trim() || !newHistRepId) return
    setIsSavingHistory(true)
    try {
      await createCallHistory({
        title: newHistTitle.trim(),
        repUserId: newHistRepId,
        durationMinutes: newHistDurationMinutes,
        outcome: newHistOutcome,
        sentiment: newHistSentiment,
        summary: newHistSummary.trim(),
        keyPoints: newHistKeyPoint.trim() ? newHistKeyPoint.split('\n').filter(Boolean) : ['Call conversation documented'],
        nextAction: newHistNextAction.trim() || undefined,
      })
      setNewHistTitle('')
      setNewHistSummary('')
      setNewHistKeyPoint('')
      setNewHistNextAction('')
      setIsAddingHistory(false)
      setSaveMessage('Call history record added!')
      setTimeout(() => setSaveMessage(null), 2500)
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'add this call history record'))
    } finally {
      setIsSavingHistory(false)
    }
  }

  const handleStartEditHistory = (item: LeadCallHistoryItem) => {
    setEditingHistoryId(item.id)
    setNewHistTitle(item.title)
    setNewHistRepId(item.rep?.id || '')
    setNewHistDurationMinutes(item.durationMinutes)
    setNewHistOutcome(item.outcome)
    setNewHistSentiment(item.sentiment)
    setNewHistSummary(item.summary)
    setNewHistKeyPoint(item.keyPoints ? item.keyPoints.join('\n') : '')
    setNewHistNextAction(item.nextAction || '')
    setIsAddingHistory(false)
  }

  const handleCancelEditHistory = () => {
    setEditingHistoryId(null)
    setNewHistTitle('')
    setNewHistSummary('')
    setNewHistKeyPoint('')
    setNewHistNextAction('')
  }

  const handleUpdateHistory = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingHistoryId || !newHistTitle.trim() || !newHistSummary.trim() || !newHistRepId) return
    setIsSavingHistoryEdit(true)
    try {
      await updateCallHistory(editingHistoryId, {
        title: newHistTitle.trim(),
        repUserId: newHistRepId,
        durationMinutes: newHistDurationMinutes,
        outcome: newHistOutcome,
        sentiment: newHistSentiment,
        summary: newHistSummary.trim(),
        keyPoints: newHistKeyPoint.trim() ? newHistKeyPoint.split('\n').filter(Boolean) : ['Call conversation documented'],
        nextAction: newHistNextAction.trim() || undefined,
      })
      setEditingHistoryId(null)
      setSaveMessage('Call history record updated!')
      setTimeout(() => setSaveMessage(null), 2500)
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'update this call history record'))
    } finally {
      setIsSavingHistoryEdit(false)
    }
  }

  const handleDeleteHistory = async (id: string) => {
    try {
      await deleteCallHistory(id)
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'delete this call history record'))
    }
  }

  // Appointment Actions
  const handleSaveAppt = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newApptTitle.trim() || !newApptScheduledAt || !newApptHostId) return
    if (apptConflicts.length > 0) {
      toast.error('Scheduling conflict — pick a time when everyone invited is free.')
      return
    }
    setIsSavingAppt(true)
    try {
      const input: CreateLeadMeetingInput = {
        title: newApptTitle.trim(),
        scheduledAt: toIndianIsoString(newApptScheduledAt),
        durationMinutes: newApptDurationMinutes,
        type: newApptType,
        hostUserId: newApptHostId,
        attendeeUserIds: newApptAttendeeIds,
        meetingUrl: newApptUrl.trim() || undefined,
        agenda: newApptAgenda.trim() || undefined,
      }
      await createMeeting(input)
      setNewApptTitle('')
      setNewApptAgenda('')
      setNewApptUrl('')
      setNewApptAttendeeIds([])
      setIsAddingAppt(false)
      setSaveMessage('Appointment scheduled — invited members notified!')
      setTimeout(() => setSaveMessage(null), 2500)
      toast.success(`Meeting "${input.title}" scheduled and invited members notified.`, { icon: '📅' })
      refreshMeetingWidgets('my-meetings-calendar', 'meetings-reminders')
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'schedule this meeting'))
    } finally {
      setIsSavingAppt(false)
    }
  }

  const handleStartEditAppt = (appt: LeadMeeting) => {
    setEditingApptId(appt.id)
    setNewApptTitle(appt.title)
    try {
      const d = new Date(appt.scheduledAt)
      const pad = (n: number) => String(n).padStart(2, '0')
      const localIso = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
      setNewApptScheduledAt(localIso)
    } catch {
      setNewApptScheduledAt('')
    }
    setNewApptDurationMinutes(appt.durationMinutes)
    setNewApptType(appt.type)
    setNewApptHostId(appt.host?.id || '')
    setNewApptAttendeeIds((appt.attendees || []).map((a) => a.id))
    setNewApptUrl(appt.meetingUrl || '')
    setNewApptAgenda(appt.agenda || '')
    setNewApptStatus(appt.status)
    setIsAddingAppt(false)
  }

  const handleCancelEditAppt = () => {
    setEditingApptId(null)
    setNewApptTitle('')
    setNewApptAgenda('')
    setNewApptUrl('')
    setNewApptAttendeeIds([])
  }

  const handleUpdateAppt = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingApptId || !newApptTitle.trim() || !newApptScheduledAt || !newApptHostId) return
    if (apptConflicts.length > 0) {
      toast.error('Scheduling conflict — pick a time when everyone invited is free.')
      return
    }
    setIsSavingApptEdit(true)
    try {
      const input: UpdateLeadMeetingInput = {
        title: newApptTitle.trim(),
        scheduledAt: new Date(newApptScheduledAt).toISOString(),
        durationMinutes: newApptDurationMinutes,
        type: newApptType,
        hostUserId: newApptHostId,
        attendeeUserIds: newApptAttendeeIds,
        meetingUrl: newApptUrl.trim() || undefined,
        agenda: newApptAgenda.trim() || undefined,
        status: newApptStatus,
      }
      await updateMeeting(editingApptId, input)
      setEditingApptId(null)
      setSaveMessage('Appointment updated!')
      setTimeout(() => setSaveMessage(null), 2500)
      toast.success(`Meeting "${input.title}" updated.`, { icon: '📅' })
      refreshMeetingWidgets('my-meetings-calendar', 'meetings-reminders')
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'update this meeting'))
    } finally {
      setIsSavingApptEdit(false)
    }
  }

  const handleDeleteAppt = async (id: string) => {
    try {
      await deleteMeetingApi(id)
      refreshMeetingWidgets('my-meetings-calendar', 'meetings-reminders')
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'cancel this meeting'))
    }
  }

  // Pin/Unpin lead
  const handlePinToggle = async () => {
    const targetLead = leadData || lead
    if (!targetLead?.id) return
    try {
      const newPinnedState = !pinned
      await api.patch(`/leads/${targetLead.id}/pin`, { pinned: newPinnedState })
      // Update local state
      setPinned(newPinnedState)
      const updated = { ...targetLead, pinned: newPinnedState }
      setLeadData(updated)
      // Notify parent if needed
      if (onUpdate) {
        onUpdate(updated)
      }
      toast.success(newPinnedState ? 'Lead pinned' : 'Lead unpinned')
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'toggle pin status'))
    }
  }

  // Save changes to backend
  const persistChanges = async (updates: Partial<LeadDetailData>) => {
    if (!lead) return
    setIsSaving(true)
    const updatedLead: LeadDetailData = {
      ...lead,
      id: lead.id,
      name: updates.name !== undefined ? updates.name : name,
      company: updates.company !== undefined ? updates.company : company,
      contactPerson: updates.contactPerson !== undefined ? updates.contactPerson : contactPerson,
      email: updates.email !== undefined ? updates.email : email,
      phone: updates.phone !== undefined ? updates.phone : phone,
      website: updates.website !== undefined ? updates.website : website,
      address: updates.address !== undefined ? updates.address : address,
      source: updates.source !== undefined ? updates.source : source,
      status: updates.status !== undefined ? updates.status : status,
      interest: updates.interest !== undefined ? updates.interest : interest,
      rating: updates.rating !== undefined ? updates.rating : rating,
      assignee: updates.assignee !== undefined ? updates.assignee : assignee,
      notes: updates.notes !== undefined ? updates.notes : notes,
      value: updates.value !== undefined ? updates.value : value,
      services: updates.services !== undefined ? updates.services : services,
      industry: updates.industry !== undefined ? updates.industry : selectedIndustry,
    }

    // Save to the backend first — everything below (parent list, the in-app
    // notification feed, "Saved" indicator) used to run unconditionally
    // before this request even ran, so a rejected save (no permission, etc.)
    // still showed "Status Updated" / "Saved" with no way to tell it hadn't.
    try {
      const numericVal = parseFloat(String(updatedLead.value).replace(/[^0-9.]/g, '')) || 0
      updatedLead.value = numericVal > 0 ? `₹${numericVal.toLocaleString('en-IN')}` : ''
      if (lead?.id && !lead.id.startsWith('lead-')) {
        await api.patch(`/leads/${lead.id}`, {
          name: updatedLead.name,
          company: updatedLead.company,
          contactPerson: updatedLead.contactPerson,
          // '' means "no email" — omit it entirely so leads without an
          // address don't fail the server's email validation.
          email: updatedLead.email || undefined,
          // Leads created before email/phone were mandatory have none —
          // send undefined so editing them doesn't fail validation.
          phone: updatedLead.phone || undefined,
          source: updatedLead.source,
          status: updatedLead.status,
          interest: updatedLead.interest,
          rating: updatedLead.rating ?? null,
          // Only sent when it changed: the server re-validates the assignee
          // every time, and an unrelated edit shouldn't fail over it.
          ...(updates.assignee !== undefined ? { assignee: updatedLead.assignee } : {}),
          value: numericVal,
          notes: updatedLead.notes,
          // Sent only when actually edited: a lead opened from a page whose
          // mapper has no address would otherwise save an empty one over the
          // stored value.
          ...(updates.website !== undefined ? { website: updatedLead.website } : {}),
          ...(updates.address !== undefined ? { address: updatedLead.address } : {}),
          services: updatedLead.services,
          industry: updatedLead.industry || undefined,
        }, { skipErrorToast: true })
      }
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'save changes to this lead'))
      setIsSaving(false)
      return
    }

    onUpdate?.(updatedLead)

    setSaveMessage('Saved')
    setTimeout(() => setSaveMessage(null), 2000)
    setIsSaving(false)
  }

  // A lead that never came from the scraper has no Maps URL in its notes. This
  // searches Maps for the lead itself (address first, then its name) and keeps
  // the resulting URL on the lead, so the location card and the link survive
  // reloads — and survive the notes being rewritten by the description field.
  const storeMapsUrl = async (url: string) => {
    // A demo row has no lead record behind it; keep the link for this session only.
    if (!lead?.id || lead.id.startsWith('lead-')) {
      setMapsUrl(url)
      return
    }
    setIsFetchingMaps(true)
    try {
      await api.patch(`/leads/${lead.id}`, { mapsUrl: url }, { skipErrorToast: true })
      setMapsUrl(url)
      onUpdate?.({ ...lead, mapsUrl: url })
      toast.success('Google Maps link saved on this lead')
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'save the Google Maps link'))
    } finally {
      setIsFetchingMaps(false)
    }
  }

  const handleGetMaps = async () => {
    if (!lead) return
    const query = [name, address].map((part) => part?.trim()).filter(Boolean).join(', ')
    if (!query) {
      toast.error('Add an address or a name to this lead first')
      return
    }
    const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`
    window.open(url, '_blank', 'noopener,noreferrer')

    // Already scraped, or already saved on this lead.
    if (mapInfo.mapsUrl) return
    await storeMapsUrl(url)
  }

  // Same card, her own link: pasting a Google Maps URL pins the lead exactly like a scraped one.
  const handleSavePastedMapsLink = async () => {
    const raw = mapsLinkDraft.trim()
    if (!raw) {
      toast.error('Paste a Google Maps link first')
      return
    }
    const url = /\s/.test(raw) ? null : asSafeExternalUrl(/^[a-z]+:\/\//i.test(raw) ? raw : `https://${raw}`)
    if (!url) {
      toast.error('That is not a link — paste the Google Maps URL')
      return
    }
    setMapsLinkDraft('')
    await storeMapsUrl(url)
  }

  const handlePostComment = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!replyText.trim()) return
    try {
      await createComment(replyText.trim())
      setReplyText('')
    } catch {
      toast.error('Failed to post comment')
    }
  }

  const handleAddTodo = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newTodoText.trim()) return
    try {
      await createTodo({ text: newTodoText.trim() })
      setNewTodoText('')
      setIsAddingTodo(false)
    } catch {
      toast.error('Failed to add todo')
    }
  }

  // Waterfall Dependencies
  const [dependencies, setDependencies] = useState<
    { id: string; title: string; type: string; status: string }[]
  >([
    {
      id: 'ldep-1',
      title: 'Prospect Discovery Call & Solution Fit',
      type: 'Pre-requisite',
      status: 'Completed',
    },
  ])
  const [isAddingDependency, setIsAddingDependency] = useState(false)
  const [newDepTitle, setNewDepTitle] = useState('')

  // Reminder State
  const [isReminderModalOpen, setIsReminderModalOpen] = useState(false)
  const [reminderTitle, setReminderTitle] = useState('')
  const [reminderPreset, setReminderPreset] = useState<'tomorrow_morning' | 'in_2_days' | 'next_week' | 'custom'>('tomorrow_morning')
  const [reminderCustomDate, setReminderCustomDate] = useState('')
  const [reminderNotes, setReminderNotes] = useState('')
  const [isCreatingReminder, setIsCreatingReminder] = useState(false)

  // Recurrence State
  const [isRecurringModalOpen, setIsRecurringModalOpen] = useState(false)
  const [isRecurring, setIsRecurring] = useState(false)
  const [recurringFrequency, setRecurringFrequency] = useState('Weekly')
  const [isSavingRecurrence, setIsSavingRecurrence] = useState(false)

  // Duplicate State
  const [isDuplicating, setIsDuplicating] = useState(false)

  const handleAddDependency = (e: React.FormEvent) => {
    e.preventDefault()
    if (!newDepTitle.trim()) return
    setDependencies((prev) => [
      ...prev,
      {
        id: `dep-${Date.now()}`,
        title: newDepTitle.trim(),
        type: 'Dependency Link',
        status: 'Active',
      },
    ])
    setNewDepTitle('')
    setIsAddingDependency(false)
  }


  const titleInputRef = React.useRef<HTMLInputElement>(null)

  const handleOpenReminderModal = () => {
    setReminderTitle(`Check in with ${contactPerson || name} on pitch & research`)
    setReminderNotes('')
    setReminderPreset('tomorrow_morning')
    setIsReminderModalOpen(true)
  }

  const handleSaveReminder = async () => {
    if (!reminderTitle.trim()) return
    setIsCreatingReminder(true)
    try {
      let dueDate = new Date()
      if (reminderPreset === 'tomorrow_morning') {
        dueDate.setDate(dueDate.getDate() + 1)
        dueDate.setHours(9, 0, 0, 0)
      } else if (reminderPreset === 'in_2_days') {
        dueDate.setDate(dueDate.getDate() + 2)
        dueDate.setHours(10, 0, 0, 0)
      } else if (reminderPreset === 'next_week') {
        dueDate.setDate(dueDate.getDate() + 7)
        dueDate.setHours(9, 0, 0, 0)
      } else if (reminderCustomDate) {
        dueDate = new Date(reminderCustomDate)
      }

      if (lead?.id && !lead.id.startsWith('lead-')) {
        await api.post(`/leads/${lead.id}/reminders`, {
          title: reminderTitle.trim(),
          dueDate: dueDate.toISOString(),
          notes: reminderNotes.trim(),
        })
      }

      toast.success('Reminder set and added to notifications!')
      setSaveMessage('Reminder set')
      setIsReminderModalOpen(false)
      setTimeout(() => setSaveMessage(null), 2500)
    } catch {
      toast.error('Failed to create reminder')
    } finally {
      setIsCreatingReminder(false)
    }
  }

  const handleOpenRecurringModal = () => {
    setIsRecurring(Boolean(lead?.isRecurring))
    setRecurringFrequency(lead?.recurringFrequency || 'Weekly')
    setIsRecurringModalOpen(true)
  }

  const handleSaveRecurring = async (enabled: boolean, freq: string) => {
    setIsSavingRecurrence(true)
    try {
      if (lead?.id && !lead.id.startsWith('lead-')) {
        await api.post(`/leads/${lead.id}/make-recurring`, {
          isRecurring: enabled,
          frequency: freq,
        })
      }
      setIsRecurring(enabled)
      setRecurringFrequency(freq)
      onUpdate?.({
        ...lead!,
        isRecurring: enabled,
        recurringFrequency: freq,
      })

      toast.success(enabled ? `Lead check-in recurrence set to ${freq}` : 'Lead recurrence disabled')
      setSaveMessage(enabled ? `Recurring: ${freq}` : 'Recurrence off')
      setIsRecurringModalOpen(false)
      setTimeout(() => setSaveMessage(null), 2500)
    } catch {
      toast.error('Failed to update lead recurrence')
    } finally {
      setIsSavingRecurrence(false)
    }
  }

  // The superadmin removes the lead here and now. Anyone else only raises a
  // request: the row — with the call logs, history, recordings, followups,
  // meetings and comments that describe it — is deleted by the server when the
  // superadmin approves, and nothing moves if they refuse.
  const handleDelete = async () => {
    if (!lead) return
    if (!isDatabaseId(lead.id)) {
      onDeleted?.(lead.id)
      setIsDeleteOpen(false)
      onClose()
      return
    }
    const reason = deleteReason.trim()
    if (!mayDeleteAlone && reason.length < 3) {
      toast.error('Say why this lead should be deleted — the superadmin reads this.')
      return
    }
    setIsDeleting(true)
    try {
      const result = await deleteRecord.mutateAsync({ entityType: 'lead', entityId: lead.id, reason })
      if (result.deleted) {
        onDeleted?.(lead.id)
        toast.success(result.message || `"${lead.name || 'This lead'}" and its activity were deleted.`)
        setIsDeleteOpen(false)
        onClose()
      } else {
        toast.success(result.message || 'Delete request sent to the superadmin for approval.')
        setIsDeleteOpen(false)
      }
      setDeleteReason('')
    } catch (err) {
      toast.error(getApiErrorMessage(err, mayDeleteAlone ? 'delete this lead' : 'send this delete request'))
    } finally {
      setIsDeleting(false)
    }
  }

  const handleWithdrawDelete = async () => {
    if (!pendingDelete) return
    try {
      await cancelDeleteRequest.mutateAsync(pendingDelete.id)
      toast.success('Delete request withdrawn. The lead stays.')
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'withdraw this delete request'))
    }
  }

  const handleDuplicate = async () => {
    if (!lead) return
    setIsDuplicating(true)
    try {
      if (lead?.id && !lead.id.startsWith('lead-')) {
        const res = await api.post<any>(`/leads/${lead.id}/duplicate`)
        const duplicatedLead = res?.data
        if (duplicatedLead) {
          queryClient.invalidateQueries({ queryKey: ['leads'] })
          toast.success(`Lead duplicated successfully as "${duplicatedLead.name}"!`)
          setSaveMessage('Lead duplicated successfully!')
          setTimeout(() => setSaveMessage(null), 2500)
          return
        }
      }

      // Fallback for mock leads
      const duplicated: LeadDetailData = {
        ...lead,
        id: `lead-${Date.now()}`,
        name: `${name} (Copy)`,
        company,
        contactPerson,
        email,
        phone,
        source,
        status: 'New',
        interest,
        assignee,
        notes,
        value,
        number: String(Math.floor(Math.random() * 90) + 10),
      }
      onUpdate?.(duplicated)
      toast.success(`Created copy "${name} (Copy)"`)
      setSaveMessage('Lead duplicated successfully!')
      setTimeout(() => setSaveMessage(null), 2500)
    } catch {
      toast.error('Failed to duplicate lead')
    } finally {
      setIsDuplicating(false)
    }
  }

  const handlePrioritySelect = (newPriority: string) => {
    setPriority(newPriority)
    persistChanges({ priority: newPriority } as any)
    setSaveMessage(`Priority: ${newPriority}`)
    setTimeout(() => setSaveMessage(null), 2000)
  }

  const handleMoveToDeal = async (stageOverride?: string) => {
    if (lead?.convertedDealId) {
      toast.error('This lead has already been converted to a deal.')
      navigate('/app/deals')
      return
    }
    const chosenStage = stageOverride || targetDealStage || 'Discovery'
    const isWon =
      chosenStage.toLowerCase() === 'closed won' ||
      dealStages.some((d) => (d.isWon || d.name.toLowerCase() === 'closed won') && d.name.toLowerCase() === chosenStage.toLowerCase())
    if (isWon) {
      toast.error('Leads cannot be directly converted into Closed Won. Please choose an active pipeline stage (e.g. Qualification, Negotiation).')
      return
    }
    setIsConvertingDeal(true)
    try {
      const rawVal = parseFloat(String(targetDealValue || value || 0).replace(/[^0-9.]/g, '')) || 0
      const clientName =
        company && company !== '—' && company !== '--' && company.trim()
          ? company.trim()
          : (name?.trim() || 'Client')
      const dealTitle =
        (targetDealTitle && targetDealTitle.trim()) ||
        `Deal: ${name}${company && company !== '—' ? ` (${company})` : ''}` ||
        'New Sales Deal'

      const prob =
        chosenStage === 'Proposal'
          ? 70
          : chosenStage === 'Negotiation'
          ? 85
          : chosenStage === 'Closed Won'
          ? 100
          : chosenStage === 'Demo & Validation'
          ? 60
          : 50

              const dealPayload = {
          title: dealTitle,
          client: clientName,
          stage: chosenStage,
          priority: priority || 'Normal',
          value: rawVal,
          probability: prob,
          owner: assignee || '',
          closeDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          }),
          notes: `Converted from Lead: ${name} (Contact: ${contactPerson || name}, Email: ${email || 'N/A'}, Phone: ${phone || 'N/A'}, Source: ${source || 'N/A'}).\n${notes || ''}`,
          organizationId: lead?.organizationId || undefined,
          services: services || [],
          contactPerson: contactPerson || name,
          // the lead's Job Title / Role
          contactDesignation: (lead as any)?.role || undefined,
          contactEmail: email || undefined,
          contactPhone: phone || undefined,
          website: (lead as any)?.website || undefined,
          address: lead?.address || undefined,
          source: source || undefined,
          billingAddress: lead?.address || undefined,
          clientAddress: lead?.address || undefined,
          gstin: (lead as any)?.gstin || (lead as any)?.clientGstin || undefined,
          clientGstin: (lead as any)?.clientGstin || (lead as any)?.gstin || undefined,
          // Lets the backend stamp convertedDealId back onto the lead and
          // reject this call with 409 if it's already been converted before —
          // guards against creating a duplicate deal from the same lead.
          leadId: lead?.id,
        }

      // Create Deal in system via GoodDay REST API
      await api.post<any>('/deals', dealPayload, { skipErrorToast: true })

      // Invalidate queries so deals, leads, and contacts refresh immediately
      try {
        queryClient.invalidateQueries({ queryKey: ['leads'] })
        queryClient.invalidateQueries({ queryKey: ['deals'] })
        queryClient.invalidateQueries({ queryKey: ['contacts'] })
      } catch {}

      // Update lead status to Qualified in local state and database
      setStatus('Qualified')
      persistChanges({ status: 'Qualified' })

      setSaveMessage(`Converted to Deal (${chosenStage})! Redirecting...`)
      setIsMoveToDealOpen(false)

      setTimeout(() => {
        setSaveMessage(null)
        onClose()
        navigate('/app/deals')
      }, 700)
    } catch (err: any) {
      const errMsg = getApiErrorMessage(err, 'convert this lead to a deal')
      toast.error(errMsg)
      setSaveMessage(`Error: ${errMsg}`)
      setTimeout(() => setSaveMessage(null), 3500)
    } finally {
      setIsConvertingDeal(false)
    }
  }

  const handleAddCustomLink = (e?: React.FormEvent) => {
    if (e) e.preventDefault()
    if (!newLinkUrl.trim() || newLinkUrl === 'https://') return
    const newLink = {
      id: `link-${Date.now()}`,
      url: newLinkUrl.trim(),
      label: newLinkLabel.trim() || newLinkUrl.trim(),
    }
    setCustomLinks((prev) => [...prev, newLink])
    setNewLinkUrl('https://')
    setNewLinkLabel('')
    setIsAddingLink(false)
    setSaveMessage('Link attached')
    setTimeout(() => setSaveMessage(null), 2000)
  }

  const handleRemoveCustomLink = (id: string) => {
    setCustomLinks((prev) => prev.filter((l) => l.id !== id))
  }

  const handleRename = () => {
    titleInputRef.current?.focus()
    titleInputRef.current?.select()
  }

  const STATUS_OPTIONS = leadStatuses
    .filter((s) => s.name.toLowerCase() !== 'closed won')
    .map((s) => ({
      label: s.name.toUpperCase(),
      value: s.name,
      color: s.color || '#6366f1',
    }))
  const SOURCE_OPTIONS = leadSources.map((s) => s.name)
  // Target stages offered when converting this lead into a deal. Closed Won is locked.
  const DEAL_STAGES = dealStages.map((s) => ({
    label: s.name,
    value: s.name,
    color: s.color || '#6366f1',
    isWon: Boolean(s.isWon) || s.name.toLowerCase() === 'closed won',
  }))
  const currentStatusObj =
    STATUS_OPTIONS.find((s) => s.value.toLowerCase() === (status || '').toLowerCase()) ||
    STATUS_OPTIONS[0] || { label: status?.toUpperCase() || '', value: status || '', color: '#6366f1' }
  const currentInterestObj = INTEREST_OPTIONS.find((i) => i.value.toLowerCase() === (interest || '').toLowerCase()) || INTEREST_OPTIONS[0]

  if (!isOpen || !lead) return null

  const leadNumberDisplay = lead.number || (lead.id?.startsWith('lead-') ? lead.id.replace('lead-', '') : '27')

  return (
    <>
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        hideCloseButton={true}
        onInteractOutside={(e) => {
          // The call-script panel portals to <body> outside this dialog; without this guard
          // Radix treats any click/focus on it as an outside interaction and closes the lead.
          if ((e.target as Element | null)?.closest?.('[data-call-script-panel]')) {
            e.preventDefault();
          }
        }}
        className={`p-0 gap-0 overflow-hidden bg-background border border-border shadow-2xl transition-all duration-200 flex flex-col ${
          isFullscreen
            ? 'w-[98vw] max-w-[98vw] h-[96vh] rounded-xl'
            : 'w-[95vw] max-w-[1240px] xl:max-w-[1340px] h-[90vh] max-h-[920px] rounded-2xl'
        }`}
      >
        {/* Top Header Bar */}
        <div className="flex items-center justify-between px-6 py-3.5 border-b border-border bg-muted/20 select-none shrink-0">
          {/* Left Breadcrumbs & Item Number */}
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <span className="font-mono font-semibold px-2 py-0.5 rounded bg-muted text-foreground/80">
              #{leadNumberDisplay}
            </span>
            <div className="flex items-center gap-1.5">
              <Link to="/app/deals" className="hover:text-foreground transition-colors">
                Sales
              </Link>
              <span>›</span>
              <Link to="/app/leads" className="hover:text-foreground transition-colors">
                Leads
              </Link>
              <span>›</span>
              <Link to={currentSectionHref || '/app/leads'} className="hover:text-foreground transition-colors font-medium text-foreground">
                {currentSectionName}
              </Link>
            </div>
          </div>

          {/* Right Window & Header Controls */}
          <div className="flex items-center gap-2">
            {saveMessage && (
              <span className="text-xs text-emerald-600 font-medium px-2 py-0.5 bg-emerald-500/10 rounded animate-in fade-in">
                {saveMessage}
              </span>
            )}
            <button
              type="button"
              title={pinned ? "Unpin Item" : "Pin Item"}
              onClick={handlePinToggle}
              className={`p-1.5 rounded-lg transition-colors ${
                pinned
                  ? 'text-amber-500 hover:bg-amber-500/10'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/60'
              }`}
            >
              <Pin className={`h-4 w-4 ${pinned ? 'fill-current' : ''}`} />
            </button>
            <button
              onClick={() => setIsFullscreen(!isFullscreen)}
              title={isFullscreen ? 'Restore' : 'Fullscreen'}
              className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors"
            >
              {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </button>
            <button
              onClick={onClose}
              title="Close"
              className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors ml-1"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Main 2-Column Body */}
        <div className="flex-1 flex flex-col md:flex-row overflow-hidden min-h-0">
          {/* LEFT COLUMN: Main content, description, todos, messages */}
          <div className="flex-1 min-w-0 flex flex-col border-r border-border overflow-y-auto p-6 space-y-6">
            {/* Title & Quick Actions Row */}
            <div className="flex items-start justify-between gap-4">
              <div className="flex flex-col gap-2 flex-1">
                <div className="flex items-center gap-3">
                  <span className="p-2 rounded-xl bg-blue-500/10 text-blue-500">
                    <Droplet className="h-5 w-5 fill-blue-500/20" />
                  </span>
                  <input
                    ref={titleInputRef}
                    type="text"
                    value={name}
                    onChange={(e) => {
                      setName(e.target.value)
                    }}
                    onBlur={() => persistChanges({ name })}
                    className="text-xl font-bold text-foreground bg-transparent border-b border-transparent hover:border-border focus:border-primary focus:outline-none w-full py-0.5"
                    placeholder="Lead Title / Name"
                  />
                </div>
                
                {services && services.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 ml-[52px]">
                    {services.map((s) => (
                      <Badge key={s} variant="secondary" className="text-[10px] font-semibold text-purple-700 bg-purple-500/10 dark:text-purple-300">
                        {s}
                      </Badge>
                    ))}
                  </div>
                )}

                {isRecurring && (
                  <div className="flex items-center gap-1.5 ml-[52px]">
                    <Badge variant="outline" className="text-[11px] font-semibold text-purple-600 dark:text-purple-400 bg-purple-500/10 border-purple-500/30 flex items-center gap-1.5 py-0.5">
                      <Repeat className="h-3 w-3" /> Recurring: {recurringFrequency || 'Weekly'}
                    </Badge>
                  </div>
                )}
              </div>

              {/* Action Icons with Script & + Add Dropdown */}
              <div className="flex items-center gap-1.5 text-muted-foreground shrink-0">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    const script = selectedIndustry ? getScriptByIndustry(selectedIndustry) : null
                    openScript(script || selectedIndustry || 'web_development', name || 'Lead')
                  }}
                  className="h-7 text-xs gap-1 shadow-xs border-indigo-200 text-indigo-700 bg-indigo-50/50 hover:bg-indigo-100 dark:border-indigo-800 dark:text-indigo-300 dark:bg-indigo-950/30 font-semibold cursor-pointer"
                  title="Open Sales Playbook & Call Script"
                >
                  <BookOpen className="h-3.5 w-3.5 text-indigo-600 dark:text-indigo-400" />
                  <span>Script</span>
                </Button>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="sm" variant="outline" className="h-7 text-xs gap-1 shadow-xs hover:bg-accent hover:text-accent-foreground">
                      <Plus className="h-3.5 w-3.5" /> Add
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-64">
                    <DropdownMenuLabel className="text-xs text-muted-foreground font-semibold">
                      Add & Convert Actions
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />

                    {/* 0. Move to Deal (With Stages Submenu) */}
                    {lead?.convertedDealId ? (
                      <DropdownMenuItem
                        onClick={() => navigate('/app/deals')}
                        className="cursor-pointer flex items-center gap-2.5 text-xs py-2 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 font-semibold rounded-md hover:bg-emerald-500/20 mb-1"
                      >
                        <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
                        <div>
                          <p className="font-bold">Already converted</p>
                          <p className="text-[10px] text-muted-foreground font-normal">View the linked deal</p>
                        </div>
                      </DropdownMenuItem>
                    ) : (
                      <DropdownMenuSub>
                        <DropdownMenuSubTrigger className="cursor-pointer flex items-center justify-between text-xs py-2 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 font-semibold rounded-md hover:bg-emerald-500/20 mb-1">
                          <div className="flex items-center gap-2">
                            <BadgeDollarSign className="h-4 w-4 text-emerald-600 shrink-0" />
                            <div>
                              <p className="font-bold">Move to Deal</p>
                              <p className="text-[10px] text-muted-foreground font-normal">Convert lead to active deal</p>
                            </div>
                          </div>
                        </DropdownMenuSubTrigger>
                        <DropdownMenuSubContent className="p-2 w-60 space-y-1 bg-popover border border-border shadow-2xl rounded-xl">
                          <DropdownMenuLabel className="text-[10px] uppercase font-bold text-muted-foreground px-2 py-0.5">
                            Target Sales Stage
                          </DropdownMenuLabel>
                          {DEAL_STAGES.map((s) => (
                            <DropdownMenuItem
                              key={s.value}
                              onClick={() => {
                                if (s.isWon) {
                                  toast.error('Closed Won cannot be set directly from a lead.')
                                  return
                                }
                                handleMoveToDeal(s.value)
                              }}
                              disabled={isConvertingDeal || s.isWon}
                              className={`flex items-center justify-between text-xs py-1.5 px-2 font-medium rounded-lg ${
                                s.isWon
                                  ? 'opacity-40 cursor-not-allowed bg-muted/40 text-muted-foreground'
                                  : 'cursor-pointer hover:bg-muted'
                              }`}
                              title={s.isWon ? 'Locked: Leads cannot be converted directly into Closed Won' : undefined}
                            >
                              <span className="flex items-center gap-2">
                                {s.isWon ? (
                                  <Lock className="h-3.5 w-3.5 text-muted-foreground" />
                                ) : (
                                  <Kanban className="h-3.5 w-3.5 text-muted-foreground" />
                                )}
                                <span>{s.label}</span>
                                {s.isWon && <span className="text-[10px] text-muted-foreground font-normal">(Locked)</span>}
                              </span>
                              <span
                                className="text-[9px] px-1.5 py-0.5 rounded font-bold text-white"
                                style={{ backgroundColor: s.color }}
                              >
                                {s.value}
                              </span>
                            </DropdownMenuItem>
                          ))}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => {
                              setTargetDealTitle(`Deal: ${name}${company ? ` (${company})` : ''}`)
                              setTargetDealValue(value)
                              setTargetDealStage('Discovery')
                              setIsMoveToDealOpen(true)
                            }}
                            className="cursor-pointer text-xs py-1.5 px-2 text-primary font-bold flex items-center gap-1.5 hover:bg-primary/10 rounded-lg"
                          >
                            <ArrowRight className="h-3.5 w-3.5" /> Configure & Move...
                          </DropdownMenuItem>
                        </DropdownMenuSubContent>
                      </DropdownMenuSub>
                    )}

                    <DropdownMenuSeparator />

                    {/* 1. TODO list */}
                    <DropdownMenuItem
                      onClick={() => {
                        setVisibleSections((prev) => ({ ...prev, todos: true }))
                        setIsAddingTodo(true)
                      }}
                      className="cursor-pointer flex items-center justify-between text-xs py-2"
                    >
                      <div className="flex items-center gap-2">
                        <ListTodo className="h-4 w-4 text-emerald-600" />
                        <div>
                          <p className="font-semibold text-foreground">TODO list</p>
                          <p className="text-[10px] text-muted-foreground">Lead action checklist</p>
                        </div>
                      </div>
                    </DropdownMenuItem>


                    <DropdownMenuSeparator />

                    {/* 5. Call Log */}
                    <DropdownMenuItem
                      onClick={() => {
                        setActiveTab('calls')
                        setIsAddingCallLog(true)
                      }}
                      className="cursor-pointer flex items-center gap-2.5 text-xs py-2"
                    >
                      <PhoneCall className="h-4 w-4 text-emerald-600" />
                      <div>
                        <p className="font-semibold text-foreground">Log a Call</p>
                        <p className="text-[10px] text-muted-foreground">Record call notes, rep & outcome</p>
                      </div>
                    </DropdownMenuItem>

                    {/* Call Script */}
                    <DropdownMenuItem
                      onClick={() => {
                        const script = selectedIndustry ? getScriptByIndustry(selectedIndustry) : null
                        openScript(script || selectedIndustry || 'web_development', name || 'Lead')
                      }}
                      className="cursor-pointer flex items-center gap-2.5 text-xs py-2"
                    >
                      <BookOpen className="h-4 w-4 text-indigo-600" />
                      <div>
                        <p className="font-semibold text-foreground">Sales Call Script</p>
                        <p className="text-[10px] text-muted-foreground">Interactive pitch, discovery & objections</p>
                      </div>
                    </DropdownMenuItem>

                    {/* 6. Follow-up */}
                    <DropdownMenuItem
                      onClick={() => {
                        setActiveTab('followups')
                        setIsAddingFollowup(true)
                      }}
                      className="cursor-pointer flex items-center gap-2.5 text-xs py-2"
                    >
                      <Clock className="h-4 w-4 text-amber-600" />
                      <div>
                        <p className="font-semibold text-foreground">Schedule Follow-up</p>
                        <p className="text-[10px] text-muted-foreground">Set task, due date & assigned rep</p>
                      </div>
                    </DropdownMenuItem>

                    {/* 7. Call History */}
                    <DropdownMenuItem
                      onClick={() => {
                        setActiveTab('callHistory')
                        setIsAddingHistory(true)
                      }}
                      className="cursor-pointer flex items-center gap-2.5 text-xs py-2"
                    >
                      <History className="h-4 w-4 text-blue-600" />
                      <div>
                        <p className="font-semibold text-foreground">Add Call History</p>
                        <p className="text-[10px] text-muted-foreground">Document past meeting summary</p>
                      </div>
                    </DropdownMenuItem>

                    {/* 8. Appointment */}
                    <DropdownMenuItem
                      onClick={() => {
                        setActiveTab('appointments')
                        setIsAddingAppt(true)
                      }}
                      className="cursor-pointer flex items-center gap-2.5 text-xs py-2"
                    >
                      <CalendarDays className="h-4 w-4 text-purple-600" />
                      <div>
                        <p className="font-semibold text-foreground">Book Appointment</p>
                        <p className="text-[10px] text-muted-foreground">Google Meet, Zoom or Call</p>
                      </div>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>



                {/* Lightning Actions Dropdown (Zap) */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                      title="Quick actions & automations"
                    >
                      <Zap className="h-4 w-4 text-amber-500" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    {/* Create Reminder */}
                    <DropdownMenuItem
                      onClick={handleOpenReminderModal}
                      className="cursor-pointer flex items-center gap-2.5 text-xs py-2 text-foreground font-medium"
                    >
                      <Bell className="h-4 w-4 text-amber-500" />
                      <span>Create Reminder</span>
                    </DropdownMenuItem>

                    <DropdownMenuSeparator />

                    {/* Make recurring */}
                    <DropdownMenuItem
                      onClick={handleOpenRecurringModal}
                      className="cursor-pointer flex items-center gap-2.5 text-xs py-2 text-foreground font-medium"
                    >
                      <Repeat className="h-4 w-4 text-purple-500" />
                      <span>Make recurring</span>
                    </DropdownMenuItem>


                  </DropdownMenuContent>
                </DropdownMenu>

                {/* Settings Dropdown (Form & Layout Preferences) */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors relative"
                      title="Form & Layout Settings"
                    >
                      <Settings className="h-4 w-4" />
                      {watchStatus === 'muted' && (
                        <span className="absolute top-0.5 right-0.5 h-1.5 w-1.5 rounded-full bg-amber-500" />
                      )}
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-72 p-1.5 bg-popover border border-border shadow-2xl rounded-2xl space-y-1">
                    <div className="px-2.5 py-1.5 border-b border-border/60">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold text-foreground flex items-center gap-1.5">
                          <Settings className="h-3.5 w-3.5 text-primary" /> Lead Form Settings
                        </span>
                      </div>
                      <p className="text-[10px] text-muted-foreground mt-0.5">
                        Customize visible sections and preferences
                      </p>
                    </div>

                    {/* Notification & Watch Status */}
                    <DropdownMenuLabel className="text-[10px] uppercase font-bold text-muted-foreground px-2 pt-1 pb-0.5">
                      Notifications & Watch
                    </DropdownMenuLabel>
                    <div className="grid grid-cols-3 gap-1 px-1 pb-1">
                      <button
                        type="button"
                        onClick={() => {
                          setWatchStatus('watching')
                          toast.success(`You are now watching all updates for lead "${name}"`)
                        }}
                        className={`text-[10px] py-1 px-1.5 rounded-md font-semibold text-center transition-all ${
                          watchStatus === 'watching'
                            ? 'bg-blue-600 text-white shadow-xs'
                            : 'bg-muted/60 text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        Watching
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setWatchStatus('normal')
                          toast.success(`Notification mode set to Mentions Only for lead "${name}"`)
                        }}
                        className={`text-[10px] py-1 px-1.5 rounded-md font-semibold text-center transition-all ${
                          watchStatus === 'normal'
                            ? 'bg-blue-600 text-white shadow-xs'
                            : 'bg-muted/60 text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        Normal
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setWatchStatus('muted')
                          toast.success(`Notifications muted for lead "${name}"`)
                        }}
                        className={`text-[10px] py-1 px-1.5 rounded-md font-semibold text-center transition-all ${
                          watchStatus === 'muted'
                            ? 'bg-rose-600 text-white shadow-xs'
                            : 'bg-muted/60 text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        Muted
                      </button>
                    </div>

                    <DropdownMenuSeparator />

                    {/* Section Visibility Toggles */}
                    <DropdownMenuLabel className="text-[10px] uppercase font-bold text-muted-foreground px-2 pt-1 pb-0.5">
                      Visible Form Sections
                    </DropdownMenuLabel>

                    <div className="space-y-0.5 px-1">
                      <div
                        onClick={() =>
                          setVisibleSections((prev) => ({ ...prev, todos: !prev.todos }))
                        }
                        className="flex items-center justify-between px-2 py-1.5 rounded-lg hover:bg-muted/70 text-xs cursor-pointer font-medium"
                      >
                        <span className="flex items-center gap-2 text-foreground">
                          <ListTodo className="h-3.5 w-3.5 text-emerald-600" />
                          TODO List
                        </span>
                        {visibleSections.todos ? (
                          <Eye className="h-3.5 w-3.5 text-primary" />
                        ) : (
                          <EyeOff className="h-3.5 w-3.5 text-muted-foreground opacity-60" />
                        )}
                      </div>

                      <div
                        onClick={() =>
                          setVisibleSections((prev) => ({
                            ...prev,
                            dependencies: !prev.dependencies,
                          }))
                        }
                        className="flex items-center justify-between px-2 py-1.5 rounded-lg hover:bg-muted/70 text-xs cursor-pointer font-medium"
                      >
                        <span className="flex items-center gap-2 text-foreground">
                          <GitMerge className="h-3.5 w-3.5 text-indigo-600" />
                          Dependencies (Waterfall)
                        </span>
                        {visibleSections.dependencies ? (
                          <Eye className="h-3.5 w-3.5 text-primary" />
                        ) : (
                          <EyeOff className="h-3.5 w-3.5 text-muted-foreground opacity-60" />
                        )}
                      </div>

                      <div
                        onClick={() =>
                          setVisibleSections((prev) => ({
                            ...prev,
                            customFields: !prev.customFields,
                          }))
                        }
                        className="flex items-center justify-between px-2 py-1.5 rounded-lg hover:bg-muted/70 text-xs cursor-pointer font-medium"
                      >
                        <span className="flex items-center gap-2 text-foreground">
                          <LinkIcon className="h-3.5 w-3.5 text-purple-600" />
                          Custom Fields & Links
                        </span>
                        {visibleSections.customFields ? (
                          <Eye className="h-3.5 w-3.5 text-primary" />
                        ) : (
                          <EyeOff className="h-3.5 w-3.5 text-muted-foreground opacity-60" />
                        )}
                      </div>

                      <div
                        onClick={() =>
                          setVisibleSections((prev) => ({ ...prev, crm: !prev.crm }))
                        }
                        className="flex items-center justify-between px-2 py-1.5 rounded-lg hover:bg-muted/70 text-xs cursor-pointer font-medium"
                      >
                        <span className="flex items-center gap-2 text-foreground">
                          <Building className="h-3.5 w-3.5 text-slate-500" />
                          CRM Account & Contacts
                        </span>
                        {visibleSections.crm ? (
                          <Eye className="h-3.5 w-3.5 text-primary" />
                        ) : (
                          <EyeOff className="h-3.5 w-3.5 text-muted-foreground opacity-60" />
                        )}
                      </div>

                      {/* Call Logs Toggle */}
                      <div
                        onClick={() =>
                          setVisibleSections((prev) => ({ ...prev, callLogs: !prev.callLogs }))
                        }
                        className="flex items-center justify-between px-2 py-1.5 rounded-lg hover:bg-muted/70 text-xs cursor-pointer font-medium"
                      >
                        <span className="flex items-center gap-2 text-foreground">
                          <PhoneCall className="h-3.5 w-3.5 text-emerald-600" />
                          Call Logs
                        </span>
                        {visibleSections.callLogs ? (
                          <Eye className="h-3.5 w-3.5 text-primary" />
                        ) : (
                          <EyeOff className="h-3.5 w-3.5 text-muted-foreground opacity-60" />
                        )}
                      </div>

                      {/* Followups Toggle */}
                      <div
                        onClick={() =>
                          setVisibleSections((prev) => ({ ...prev, followups: !prev.followups }))
                        }
                        className="flex items-center justify-between px-2 py-1.5 rounded-lg hover:bg-muted/70 text-xs cursor-pointer font-medium"
                      >
                        <span className="flex items-center gap-2 text-foreground">
                          <Clock className="h-3.5 w-3.5 text-amber-600" />
                          Followups
                        </span>
                        {visibleSections.followups ? (
                          <Eye className="h-3.5 w-3.5 text-primary" />
                        ) : (
                          <EyeOff className="h-3.5 w-3.5 text-muted-foreground opacity-60" />
                        )}
                      </div>

                      {/* Call History Toggle */}
                      <div
                        onClick={() =>
                          setVisibleSections((prev) => ({ ...prev, callHistory: !prev.callHistory }))
                        }
                        className="flex items-center justify-between px-2 py-1.5 rounded-lg hover:bg-muted/70 text-xs cursor-pointer font-medium"
                      >
                        <span className="flex items-center gap-2 text-foreground">
                          <History className="h-3.5 w-3.5 text-blue-600" />
                          Call History
                        </span>
                        {visibleSections.callHistory ? (
                          <Eye className="h-3.5 w-3.5 text-primary" />
                        ) : (
                          <EyeOff className="h-3.5 w-3.5 text-muted-foreground opacity-60" />
                        )}
                      </div>

                      {/* Call Recordings Toggle */}
                      <div
                        onClick={() =>
                          setVisibleSections((prev) => ({ ...prev, recordings: !prev.recordings }))
                        }
                        className="flex items-center justify-between px-2 py-1.5 rounded-lg hover:bg-muted/70 text-xs cursor-pointer font-medium"
                      >
                        <span className="flex items-center gap-2 text-foreground">
                          <Mic className="h-3.5 w-3.5 text-rose-600" />
                          Call Recordings
                        </span>
                        {visibleSections.recordings ? (
                          <Eye className="h-3.5 w-3.5 text-primary" />
                        ) : (
                          <EyeOff className="h-3.5 w-3.5 text-muted-foreground opacity-60" />
                        )}
                      </div>

                      {/* Appointments Toggle */}
                      <div
                        onClick={() =>
                          setVisibleSections((prev) => ({ ...prev, appointments: !prev.appointments }))
                        }
                        className="flex items-center justify-between px-2 py-1.5 rounded-lg hover:bg-muted/70 text-xs cursor-pointer font-medium"
                      >
                        <span className="flex items-center gap-2 text-foreground">
                          <CalendarDays className="h-3.5 w-3.5 text-purple-600" />
                          Appointments
                        </span>
                        {visibleSections.appointments ? (
                          <Eye className="h-3.5 w-3.5 text-primary" />
                        ) : (
                          <EyeOff className="h-3.5 w-3.5 text-muted-foreground opacity-60" />
                        )}
                      </div>
                    </div>

                    <DropdownMenuSeparator />

                    {/* Comment Sorting */}
                    <div className="px-2 py-1 flex items-center justify-between text-xs">
                      <span className="text-muted-foreground flex items-center gap-1.5 font-medium">
                        <ArrowDownUp className="h-3.5 w-3.5" /> Activity Order
                      </span>
                      <button
                        type="button"
                        onClick={() =>
                          setCommentSortOrder((prev) =>
                            prev === 'oldest' ? 'newest' : 'oldest'
                          )
                        }
                        className="text-[11px] font-bold text-primary hover:underline"
                      >
                        {commentSortOrder === 'oldest' ? 'Oldest first' : 'Newest first'}
                      </button>
                    </div>

                    <DropdownMenuSeparator />

                    <div className="pt-1 pb-0.5 px-2 flex justify-between items-center text-[10px] text-muted-foreground">
                      <span>Preferences saved automatically</span>
                      <button
                        type="button"
                        onClick={() => {
                          setVisibleSections({
                            todos: true,
                            dependencies: true,
                            customFields: true,
                            crm: true,
                            callLogs: true,
                            followups: true,
                            callHistory: true,
                            recordings: true,
                            appointments: true,
                          })
                          setCommentSortOrder('oldest')
                          setWatchStatus('watching')
                          setSaveMessage('Settings reset to default')
                          setTimeout(() => setSaveMessage(null), 2000)
                        }}
                        className="text-primary hover:underline font-semibold"
                      >
                        Reset All
                      </button>
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* More Options Dropdown (3 Dots) */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                      title="More options"
                    >
                      <MoreHorizontal className="h-4 w-4" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    {/* Rename */}
                    <DropdownMenuItem
                      onClick={handleRename}
                      className="cursor-pointer flex items-center gap-2.5 text-xs py-2 text-foreground font-medium"
                    >
                      <Pencil className="h-4 w-4 text-muted-foreground" />
                      <span>Rename</span>
                    </DropdownMenuItem>

                    <DropdownMenuSeparator />

                    {/* Archive Lead */}
                    <DropdownMenuItem
                      onClick={() => {
                        if (lead?.convertedDealId) {
                          toast.error('This lead has already been converted to a deal and cannot be archived as lost.')
                          return
                        }
                        // Prefer the status literally named "Lost" over other
                        // isLost statuses like "Unqualified" — those represent a
                        // distinct, user-chosen disqualification reason and
                        // shouldn't be silently overwritten by this action.
                        const lostStatus =
                          leadStatuses.find((s) => s.isLost && s.name.toLowerCase() === 'lost') ??
                          leadStatuses.find((s) => s.isLost)
                        if (!lostStatus) {
                          toast.error('No "Lost" lead status is configured for this organization.')
                          return
                        }
                        persistChanges({ status: lostStatus.name })
                        setSaveMessage('Archived lead')
                        setTimeout(() => setSaveMessage(null), 2500)
                      }}
                      className={`cursor-pointer flex items-center gap-2.5 text-xs py-2 ${
                        lead?.convertedDealId
                          ? 'text-muted-foreground/50 cursor-not-allowed'
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      <Archive className="h-4 w-4" />
                      <span>Archive lead</span>
                    </DropdownMenuItem>

                    {/* Delete lead: the superadmin deletes, sales asks first. */}
                    {pendingDelete ? (
                      <>
                        <DropdownMenuItem
                          className="cursor-default flex items-center gap-2.5 text-xs py-2 text-amber-600 dark:text-amber-400"
                        >
                          <Clock className="h-4 w-4" />
                          <span>Delete requested — awaiting approval</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={handleWithdrawDelete}
                          className="cursor-pointer flex items-center gap-2.5 text-xs py-2 text-muted-foreground hover:text-foreground"
                        >
                          <Ban className="h-4 w-4" />
                          <span>Withdraw delete request</span>
                        </DropdownMenuItem>
                      </>
                    ) : (
                      <DropdownMenuItem
                        onClick={() => {
                          setDeleteReason('')
                          setIsDeleteOpen(true)
                        }}
                        className="cursor-pointer flex items-center gap-2.5 text-xs py-2 text-destructive hover:bg-destructive/10"
                      >
                        <Trash2 className="h-4 w-4" />
                        <span>{mayDeleteAlone ? 'Delete lead' : 'Request lead deletion'}</span>
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            {/* Lead Detail Feature Navigation Bar & Quick Action Pills */}
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border/70 pb-2.5 pt-1">
              {/* Feature Tabs */}
              <div className="flex items-center gap-1 overflow-x-auto pb-1 sm:pb-0 scrollbar-none">
                <button
                  type="button"
                  onClick={() => setActiveTab('all')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all cursor-pointer ${
                    activeTab === 'all'
                      ? 'bg-primary text-primary-foreground shadow-xs'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`}
                >
                  <Layers className="h-3.5 w-3.5" />
                  <span>Overview</span>
                </button>

                <button
                  type="button"
                  onClick={() => setActiveTab('calls')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all cursor-pointer ${
                    activeTab === 'calls'
                      ? 'bg-emerald-600 text-white shadow-xs'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`}
                >
                  <PhoneCall className="h-3.5 w-3.5 text-emerald-500" />
                  <span>Call Logs</span>
                  <span className="text-[10px] px-1.5 py-0.2 rounded-full font-mono font-bold bg-black/15 dark:bg-white/20">
                    {callLogs.length}
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => setActiveTab('followups')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all cursor-pointer ${
                    activeTab === 'followups'
                      ? 'bg-amber-600 text-white shadow-xs'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`}
                >
                  <Clock className="h-3.5 w-3.5 text-amber-500" />
                  <span>Followups</span>
                  <span className="text-[10px] px-1.5 py-0.2 rounded-full font-mono font-bold bg-black/15 dark:bg-white/20">
                    {followups.filter((f) => f.status === 'Pending').length}
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => setActiveTab('callHistory')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all cursor-pointer ${
                    activeTab === 'callHistory'
                      ? 'bg-blue-600 text-white shadow-xs'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`}
                >
                  <History className="h-3.5 w-3.5 text-blue-500" />
                  <span>Call History</span>
                  <span className="text-[10px] px-1.5 py-0.2 rounded-full font-mono font-bold bg-black/15 dark:bg-white/20">
                    {callHistory.length}
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => setActiveTab('appointments')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all cursor-pointer ${
                    activeTab === 'appointments'
                      ? 'bg-purple-600 text-white shadow-xs'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`}
                >
                  <CalendarDays className="h-3.5 w-3.5 text-purple-500" />
                  <span>Appointments</span>
                  <span className="text-[10px] px-1.5 py-0.2 rounded-full font-mono font-bold bg-black/15 dark:bg-white/20">
                    {appointments.length}
                  </span>
                </button>
              </div>


            </div>

            {/* Quick 4-Feature Overview KPI Cards */}
            {activeTab === 'all' && (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
                {/* 1. Call Logs KPI */}
                <div
                  onClick={() => setActiveTab('calls')}
                  className="p-3 rounded-xl border border-emerald-200/80 bg-emerald-50/40 dark:bg-emerald-950/20 dark:border-emerald-900/60 hover:border-emerald-400 hover:shadow-xs cursor-pointer transition-all group"
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-400 flex items-center gap-1.5">
                      <PhoneCall className="h-3.5 w-3.5" /> Call Logs
                    </span>
                    <Badge variant="outline" className="text-[10px] font-mono font-bold bg-emerald-100/70 border-emerald-300 text-emerald-800 dark:bg-emerald-900/60 dark:text-emerald-300">
                      {callLogs.length}
                    </Badge>
                  </div>
                  <p className="text-xs font-semibold text-foreground truncate group-hover:text-emerald-600 transition-colors">
                    {callLogs[0]?.outcome || 'No calls logged'}
                  </p>
                  <p className="text-[10px] text-muted-foreground truncate mt-0.5">
                    {callLogs[0] ? `${callLogs[0].rep?.name || 'Unknown'} • ${new Date(callLogs[0].occurredAt).toLocaleDateString()}` : 'Click to log call'}
                  </p>
                </div>

                {/* 2. Followups KPI */}
                <div
                  onClick={() => setActiveTab('followups')}
                  className="p-3 rounded-xl border border-amber-200/80 bg-amber-50/40 dark:bg-amber-950/20 dark:border-amber-900/60 hover:border-amber-400 hover:shadow-xs cursor-pointer transition-all group"
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-amber-700 dark:text-amber-400 flex items-center gap-1.5">
                      <Clock className="h-3.5 w-3.5" /> Followups
                    </span>
                    <Badge variant="outline" className="text-[10px] font-mono font-bold bg-amber-100/70 border-amber-300 text-amber-800 dark:bg-amber-900/60 dark:text-amber-300">
                      {followups.filter((f) => f.status === 'Pending').length}
                    </Badge>
                  </div>
                  <p className="text-xs font-semibold text-foreground truncate group-hover:text-amber-600 transition-colors">
                    {followups.find((f) => f.status === 'Pending')?.title || 'All completed'}
                  </p>
                  <p className="text-[10px] text-muted-foreground truncate mt-0.5">
                    {(() => {
                      const nextDue = followups.find((f) => f.status === 'Pending')?.dueAt
                      return nextDue ? new Date(nextDue).toLocaleString() : 'Click to schedule'
                    })()}
                  </p>
                </div>

                {/* 3. Call History KPI */}
                <div
                  onClick={() => setActiveTab('callHistory')}
                  className="p-3 rounded-xl border border-blue-200/80 bg-blue-50/40 dark:bg-blue-950/20 dark:border-blue-900/60 hover:border-blue-400 hover:shadow-xs cursor-pointer transition-all group"
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-blue-700 dark:text-blue-400 flex items-center gap-1.5">
                      <History className="h-3.5 w-3.5" /> Call History
                    </span>
                    <Badge variant="outline" className="text-[10px] font-mono font-bold bg-blue-100/70 border-blue-300 text-blue-800 dark:bg-blue-900/60 dark:text-blue-300">
                      {callHistory.length}
                    </Badge>
                  </div>
                  <p className="text-xs font-semibold text-foreground truncate group-hover:text-blue-600 transition-colors">
                    {callHistory[0]?.title || 'No history recorded'}
                  </p>
                  <p className="text-[10px] text-muted-foreground truncate mt-0.5">
                    {callHistory[0] ? `${callHistory[0].durationMinutes} min • ${new Date(callHistory[0].occurredAt).toLocaleDateString()}` : 'Click to add record'}
                  </p>
                </div>

                {/* 4. Appointments KPI */}
                <div
                  onClick={() => setActiveTab('appointments')}
                  className="p-3 rounded-xl border border-purple-200/80 bg-purple-50/40 dark:bg-purple-950/20 dark:border-purple-900/60 hover:border-purple-400 hover:shadow-xs cursor-pointer transition-all group"
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-purple-700 dark:text-purple-400 flex items-center gap-1.5">
                      <CalendarDays className="h-3.5 w-3.5" /> Appointments
                    </span>
                    <Badge variant="outline" className="text-[10px] font-mono font-bold bg-purple-100/70 border-purple-300 text-purple-800 dark:bg-purple-900/60 dark:text-purple-300">
                      {appointments.length}
                    </Badge>
                  </div>
                  <p className="text-xs font-semibold text-foreground truncate group-hover:text-purple-600 transition-colors">
                    {appointments[0]?.title || 'No appointments'}
                  </p>
                  <p className="text-[10px] text-muted-foreground truncate mt-0.5">
                    {appointments[0]
                      ? new Date(appointments[0].scheduledAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
                      : 'Click to book meeting'}
                  </p>
                </div>
              </div>
            )}

            {/* Description Area */}
            <div className="space-y-1">
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                onBlur={() => persistChanges({ notes: description })}
                placeholder="Add description..."
                rows={2}
                className="w-full text-sm text-foreground bg-transparent border border-transparent hover:border-border/60 focus:border-border rounded-lg p-2 focus:outline-none transition-colors resize-none placeholder:text-muted-foreground/60"
              />
            </div>

            {/* Location Map — sits above the todo list, where the lead's day-to-day work happens.
                A scraped lead carries its own listing link; any other lead gets the link resolved
                and stored here. */}
            {activeTab === 'all' && (
              <div className="space-y-2">
                <h3 className="text-sm font-bold text-foreground flex items-center justify-between gap-2">
                  <span className="flex items-center gap-1.5">
                    <MapPin className="h-4 w-4 text-rose-500" /> Location
                  </span>
                  {mapInfo.mapsUrl ? (
                    <a
                      href={mapInfo.mapsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={mapInfo.mapsUrl}
                      className="flex items-center gap-1 text-[11px] font-medium text-primary hover:underline"
                    >
                      <ExternalLink className="h-3 w-3" /> Google Maps
                    </a>
                  ) : (
                    <button
                      type="button"
                      onClick={handleGetMaps}
                      disabled={isFetchingMaps}
                      className="flex items-center gap-1 rounded-md border border-rose-200 bg-rose-50 px-2 py-1 text-[11px] font-semibold text-rose-600 hover:bg-rose-100 disabled:opacity-50 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-300"
                    >
                      <MapPin className="h-3 w-3" />
                      {isFetchingMaps ? 'Getting Google Maps…' : 'Get Google Maps'}
                    </button>
                  )}
                </h3>
                {!mapInfo.mapsUrl && (
                  <div className="flex items-center gap-2">
                    <input
                      type="url"
                      value={mapsLinkDraft}
                      onChange={(e) => setMapsLinkDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault()
                          void handleSavePastedMapsLink()
                        }
                      }}
                      placeholder="Or paste a Google Maps link here"
                      className="flex-1 min-w-0 text-xs px-2.5 py-1.5 rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:ring-2 focus:ring-rose-400"
                    />
                    <button
                      type="button"
                      onClick={handleSavePastedMapsLink}
                      disabled={isFetchingMaps || !mapsLinkDraft.trim()}
                      className="shrink-0 rounded-lg bg-rose-600 px-2.5 py-1.5 text-[11px] font-semibold text-white hover:bg-rose-700 disabled:opacity-50"
                    >
                      {isFetchingMaps ? 'Saving…' : 'Show map'}
                    </button>
                  </div>
                )}
                {mapInfo.embedSrc ? (
                  <div className="overflow-hidden rounded-xl border border-border/60">
                    <iframe
                      title="Lead location"
                      src={mapInfo.embedSrc}
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      className="block w-full h-64 border-0"
                    />
                  </div>
                ) : (
                  <p className="text-[11px] text-muted-foreground leading-snug">
                    No map yet. Add the address above, then use “Get Google Maps” to open it and save
                    the link on this lead.
                  </p>
                )}
                {address && (
                  <p className="text-[11px] text-muted-foreground leading-snug">{address}</p>
                )}
              </div>
            )}

            {/* Todo List */}
            {activeTab === 'all' && visibleSections.todos && (
              <div className="space-y-2">
                <h3 className="text-sm font-bold text-foreground flex items-center justify-between">
                  <span>Todo List</span>
                </h3>

                <div className="space-y-1.5">
                  {todos.map((todo) => (
                    <div
                      key={todo.id}
                      className="flex items-center gap-2.5 text-sm p-1.5 rounded-lg hover:bg-muted/40 transition-colors group"
                    >
                      <button
                        type="button"
                        onClick={() => toggleTodo(todo.id, !todo.done)}
                        className="shrink-0"
                      >
                        {todo.done ? (
                          <CheckSquare className="h-4 w-4 text-emerald-500" />
                        ) : (
                          <Square className="h-4 w-4 text-muted-foreground group-hover:text-foreground" />
                        )}
                      </button>
                      <span className={`flex-1 ${todo.done ? 'line-through text-muted-foreground' : 'text-foreground'}`}>
                        {todo.text}
                      </span>
                      <button
                        type="button"
                        onClick={() => deleteTodo(todo.id)}
                        className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-opacity shrink-0"
                        title="Delete todo"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>

                {isAddingTodo ? (
                  <form onSubmit={handleAddTodo} className="flex items-center gap-2 pt-1">
                    <Input
                      autoFocus
                      value={newTodoText}
                      onChange={(e) => setNewTodoText(e.target.value)}
                      placeholder="Enter todo item..."
                      className="h-8 text-xs"
                    />
                    <Button type="submit" size="sm" className="h-8 text-xs">
                      Add
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8 text-xs"
                      onClick={() => setIsAddingTodo(false)}
                    >
                      Cancel
                    </Button>
                  </form>
                ) : (
                  <button
                    onClick={() => setIsAddingTodo(true)}
                    className="flex items-center gap-1.5 text-xs text-primary font-medium hover:underline pt-1"
                  >
                    <Plus className="h-3.5 w-3.5" /> New todo
                  </button>
                )}
              </div>
            )}



            {/* 1. Call Logs Section */}
            {(activeTab === 'calls' || activeTab === 'all') && visibleSections.callLogs && (
              <div className="space-y-3 pt-3 border-t border-border/60">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="p-1.5 rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
                      <PhoneCall className="h-4 w-4" />
                    </span>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-bold text-foreground">Call Logs</h3>
                        <Badge variant="outline" className="text-[10px] font-mono font-bold bg-emerald-50 text-emerald-700 border-emerald-300 dark:bg-emerald-950/40 dark:text-emerald-300">
                          {callLogs.length} logged
                        </Badge>
                      </div>
                      <p className="text-[11px] text-muted-foreground">
                        Phone call records, customer feedback, and rep notes
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        const script = selectedIndustry ? getScriptByIndustry(selectedIndustry) : null
                        openScript(script || selectedIndustry || 'web_development', name || 'Lead')
                      }}
                      className="h-7 text-xs gap-1 shadow-2xs cursor-pointer border-indigo-200 text-indigo-700 bg-indigo-50/50 hover:bg-indigo-100 dark:border-indigo-800 dark:text-indigo-300 dark:bg-indigo-950/30"
                      title="Open Sales Playbook & Call Script"
                    >
                      <BookOpen className="h-3.5 w-3.5 text-indigo-600 dark:text-indigo-400" />
                      <span>Call Script</span>
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant={isAddingCallLog ? 'secondary' : 'default'}
                      onClick={() => setIsAddingCallLog(!isAddingCallLog)}
                      className="h-7 text-xs gap-1 shadow-2xs cursor-pointer"
                    >
                      <PhoneCall className="h-3 w-3" />
                      <span>{isAddingCallLog ? 'Close Form' : 'Log a Call'}</span>
                    </Button>
                  </div>
                </div>

                {/* Log Call Form */}
                {isAddingCallLog && (
                  <form onSubmit={handleSaveCallLog} className="p-3.5 rounded-xl border border-emerald-300/80 bg-emerald-50/30 dark:bg-emerald-950/20 dark:border-emerald-800 space-y-3 animate-in fade-in duration-200">
                    <div className="flex items-center justify-between pb-1 border-b border-emerald-200/60 dark:border-emerald-900/60">
                      <span className="text-xs font-bold text-emerald-800 dark:text-emerald-300 flex items-center gap-1.5">
                        <PhoneCall className="h-3.5 w-3.5" /> Record New Phone Interaction
                      </span>
                      <span className="text-[10px] text-muted-foreground">Auto-tagged with current date & time</span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                      <div>
                        <label className="text-[10px] uppercase font-bold text-muted-foreground block mb-1">Direction</label>
                        <select
                          value={newCallType}
                          onChange={(e: any) => setNewCallType(e.target.value)}
                          className="w-full h-8 text-xs rounded-lg border border-border bg-background px-2 focus:outline-none focus:border-primary"
                        >
                          <option value="Outbound">Outbound (We called)</option>
                          <option value="Inbound">Inbound (Client called)</option>
                        </select>
                      </div>

                      <div>
                        <label className="text-[10px] uppercase font-bold text-muted-foreground block mb-1">Call Outcome</label>
                        <select
                          value={newCallOutcome}
                          onChange={(e: any) => setNewCallOutcome(e.target.value)}
                          className="w-full h-8 text-xs rounded-lg border border-border bg-background px-2 focus:outline-none focus:border-primary"
                        >
                          <option value="Connected">Connected & Spoke</option>
                          <option value="Left Voicemail">Left Voicemail</option>
                          <option value="Scheduled Demo">Scheduled Demo / Follow-up</option>
                          <option value="Busy">Busy</option>
                          <option value="No Answer">No Answer</option>
                          <option value="Wrong Number">Wrong Number</option>
                        </select>
                      </div>

                      <div>
                        <label className="text-[10px] uppercase font-bold text-muted-foreground block mb-1">Duration</label>
                        <select
                          value={newCallDurationMinutes}
                          onChange={(e) => setNewCallDurationMinutes(Number(e.target.value))}
                          className="w-full h-8 text-xs rounded-lg border border-border bg-background px-2 focus:outline-none focus:border-primary"
                        >
                          <option value={5}>5 mins</option>
                          <option value={10}>10 mins</option>
                          <option value={15}>15 mins</option>
                          <option value={25}>25 mins</option>
                          <option value={45}>45 mins</option>
                          <option value={60}>1 hour</option>
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                      <div>
                        <label className="text-[10px] uppercase font-bold text-muted-foreground block mb-1">Sales Rep</label>
                        <select
                          value={newCallRepId}
                          onChange={(e) => setNewCallRepId(e.target.value)}
                          className="w-full h-8 text-xs rounded-lg border border-border bg-background px-2 focus:outline-none focus:border-primary"
                        >
                          {assignableUsers.length === 0 && <option value="">Loading...</option>}
                          {assignableUsers.map((u) => (
                            <option key={u.id} value={u.id}>
                              {u.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="text-[10px] uppercase font-bold text-muted-foreground block mb-1">Prospect Sentiment</label>
                        <select
                          value={newCallSentiment}
                          onChange={(e: any) => setNewCallSentiment(e.target.value)}
                          className="w-full h-8 text-xs rounded-lg border border-border bg-background px-2 focus:outline-none focus:border-primary"
                        >
                          <option value="Positive">Positive / High Interest</option>
                          <option value="Neutral">Neutral / Evaluating</option>
                          <option value="Hesitant">Hesitant / Budget Constraints</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="text-[10px] uppercase font-bold text-muted-foreground block mb-1">Call Discussion Notes & Next Steps</label>
                      <textarea
                        rows={2}
                        value={newCallNotes}
                        onChange={(e) => setNewCallNotes(e.target.value)}
                        placeholder="Summarize key points discussed, buyer questions, agreed action items..."
                        className="w-full text-xs rounded-lg border border-border bg-background p-2 focus:outline-none focus:border-primary resize-none"
                      />
                    </div>

                    <div className="flex items-center justify-end gap-2 pt-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs cursor-pointer"
                      onClick={() => setIsAddingCallLog(false)}
                    >
                      Cancel
                    </Button>
                    <Button type="submit" size="sm" disabled={isSavingCallLog} className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white cursor-pointer">
                      {isSavingCallLog ? 'Saving...' : 'Save Call Log'}
                    </Button>
                  </div>
                </form>
              )}

              {/* Edit Call Log Form */}
              {editingCallLogId && (
                <form onSubmit={handleUpdateCallLog} className="p-3.5 rounded-xl border border-blue-300/80 bg-blue-50/30 dark:bg-blue-950/20 dark:border-blue-800 space-y-3 animate-in fade-in duration-200">
                  <div className="flex items-center justify-between pb-1 border-b border-blue-200/60 dark:border-blue-900/60">
                    <span className="text-xs font-bold text-blue-800 dark:text-blue-300 flex items-center gap-1.5">
                      <Pencil className="h-3.5 w-3.5" /> Edit Call Log
                    </span>
                    <button type="button" onClick={handleCancelEditCallLog} className="text-[10px] text-muted-foreground hover:text-foreground cursor-pointer">
                      Cancel
                    </button>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                    <div>
                      <label className="text-[10px] uppercase font-bold text-muted-foreground block mb-1">Direction</label>
                      <select value={newCallType} onChange={(e: any) => setNewCallType(e.target.value)} className="w-full h-8 text-xs rounded-lg border border-border bg-background px-2 focus:outline-none focus:border-primary">
                        <option value="Outbound">Outbound (We called)</option>
                        <option value="Inbound">Inbound (Client called)</option>
                      </select>
                    </div>

                    <div>
                      <label className="text-[10px] uppercase font-bold text-muted-foreground block mb-1">Call Outcome</label>
                      <select value={newCallOutcome} onChange={(e: any) => setNewCallOutcome(e.target.value)} className="w-full h-8 text-xs rounded-lg border border-border bg-background px-2 focus:outline-none focus:border-primary">
                        <option value="Connected">Connected & Spoke</option>
                        <option value="Left Voicemail">Left Voicemail</option>
                        <option value="Scheduled Demo">Scheduled Demo / Follow-up</option>
                        <option value="Busy">Busy</option>
                        <option value="No Answer">No Answer</option>
                        <option value="Wrong Number">Wrong Number</option>
                      </select>
                    </div>

                    <div>
                      <label className="text-[10px] uppercase font-bold text-muted-foreground block mb-1">Duration</label>
                      <select value={newCallDurationMinutes} onChange={(e) => setNewCallDurationMinutes(Number(e.target.value))} className="w-full h-8 text-xs rounded-lg border border-border bg-background px-2 focus:outline-none focus:border-primary">
                        <option value={5}>5 mins</option>
                        <option value={10}>10 mins</option>
                        <option value={15}>15 mins</option>
                        <option value={25}>25 mins</option>
                        <option value={30}>30 mins</option>
                        <option value={45}>45 mins</option>
                        <option value={60}>1 hour</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    <div>
                      <label className="text-[10px] uppercase font-bold text-muted-foreground block mb-1">Sales Rep</label>
                      <select value={newCallRepId} onChange={(e) => setNewCallRepId(e.target.value)} className="w-full h-8 text-xs rounded-lg border border-border bg-background px-2 focus:outline-none focus:border-primary">
                        {assignableUsers.length === 0 && <option value="">Loading...</option>}
                        {assignableUsers.map((u) => (
                          <option key={u.id} value={u.id}>{u.username || u.name}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="text-[10px] uppercase font-bold text-muted-foreground block mb-1">Prospect Sentiment</label>
                      <select value={newCallSentiment} onChange={(e: any) => setNewCallSentiment(e.target.value)} className="w-full h-8 text-xs rounded-lg border border-border bg-background px-2 focus:outline-none focus:border-primary">
                        <option value="Positive">Positive / High Interest</option>
                        <option value="Neutral">Neutral / Evaluating</option>
                        <option value="Hesitant">Hesitant / Budget Constraints</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="text-[10px] uppercase font-bold text-muted-foreground block mb-1">Call Discussion Notes & Next Steps</label>
                    <textarea rows={2} value={newCallNotes} onChange={(e) => setNewCallNotes(e.target.value)} placeholder="Summarize key points discussed, buyer questions, agreed action items..." className="w-full text-xs rounded-lg border border-border bg-background p-2 focus:outline-none focus:border-primary resize-none" />
                  </div>

                  <div className="flex items-center justify-end gap-2 pt-1">
                    <Button type="button" variant="ghost" size="sm" className="h-7 text-xs cursor-pointer" onClick={handleCancelEditCallLog}>
                      Cancel
                    </Button>
                    <Button type="submit" size="sm" disabled={isSavingCallLogEdit} className="h-7 text-xs bg-blue-600 hover:bg-blue-700 text-white cursor-pointer">
                      {isSavingCallLogEdit ? 'Updating...' : 'Update Call Log'}
                    </Button>
                  </div>
                </form>
              )}

                {/* Call Logs List */}
                <div className="space-y-2">
                  {callLogs.length === 0 ? (
                    <div className="text-center py-6 border border-dashed rounded-xl bg-muted/20 text-muted-foreground text-xs">
                      No call logs recorded yet. Click "Log a Call" to record outreach.
                    </div>
                  ) : (
                    callLogs.map((log) => {
                      const isConnected = log.outcome.toLowerCase().includes('connected') || log.outcome.toLowerCase().includes('demo')
                      const isVoicemail = log.outcome.toLowerCase().includes('voicemail')
                      return (
                        <div
                          key={log.id}
                          className="p-3 rounded-xl border border-border/80 bg-card hover:bg-muted/30 transition-all space-y-2 shadow-2xs group"
                        >
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <div className="flex items-center gap-2">
                              <span className={`p-1.5 rounded-lg ${log.type === 'Outbound' ? 'bg-blue-500/10 text-blue-600 dark:text-blue-400' : 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'}`}>
                                {log.type === 'Outbound' ? <PhoneOutgoing className="h-3.5 w-3.5" /> : <PhoneIncoming className="h-3.5 w-3.5" />}
                              </span>
                              <div>
                                <div className="flex items-center gap-1.5">
                                  <span className="font-bold text-xs text-foreground">{log.type} Call</span>
                                  <span className="text-[10px] text-muted-foreground">• by {log.rep?.name || 'Unknown'}</span>
                                </div>
                                <span className="text-[10px] text-muted-foreground">{new Date(log.occurredAt).toLocaleString()}</span>
                              </div>
                            </div>

                            <div className="flex items-center gap-1.5">
                              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${
                                isConnected
                                  ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800'
                                  : isVoicemail
                                  ? 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800'
                                  : 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:border-rose-800'
                              }`}>
                                {log.outcome}
                              </span>

                              <Badge variant="outline" className="text-[10px] font-mono border-border">
                                {log.durationMinutes} min
                              </Badge>

                              {log.sentiment && (
                                <Badge variant="outline" className="text-[10px] border-border hidden sm:inline-flex">
                                  {log.sentiment}
                                </Badge>
                              )}

                              <button
                                type="button"
                                onClick={() => handleStartEditCallLog(log)}
                                className="opacity-0 group-hover:opacity-100 p-1 text-muted-foreground hover:text-primary transition-opacity cursor-pointer"
                                title="Edit Call Log"
                              >
                                <Pencil className="h-3 w-3" />
                              </button>

                              <button
                                type="button"
                                onClick={() => handleDeleteCallLog(log.id)}
                                className="opacity-0 group-hover:opacity-100 p-1 text-muted-foreground hover:text-destructive transition-opacity cursor-pointer"
                                title="Delete Call Log"
                              >
                                <Trash2 className="h-3 w-3" />
                              </button>
                            </div>
                          </div>

                          <p className="text-xs text-muted-foreground leading-relaxed pl-8">
                            {log.notes}
                          </p>
                        </div>
                      )
                    })
                  )}
                </div>
              </div>
            )}

            {/* 2. Followups Section */}
            {(activeTab === 'followups' || activeTab === 'all') && visibleSections.followups && (
              <div className="space-y-3 pt-3 border-t border-border/60">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="p-1.5 rounded-lg bg-amber-500/10 text-amber-600 dark:text-amber-400">
                      <Clock className="h-4 w-4" />
                    </span>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-bold text-foreground">Followups</h3>
                        <Badge variant="outline" className="text-[10px] font-mono font-bold bg-amber-50 text-amber-700 border-amber-300 dark:bg-amber-950/40 dark:text-amber-300">
                          {followups.filter((f) => f.status === 'Pending').length} pending
                        </Badge>
                      </div>
                      <p className="text-[11px] text-muted-foreground">
                        Scheduled customer follow-up touches, deadlines, and commitments
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {/* Followup Filter Pills */}
                    <div className="hidden sm:flex items-center gap-1 text-[11px] bg-muted/60 p-0.5 rounded-lg">
                      <button
                        type="button"
                        onClick={() => setFollowupFilter('all')}
                        className={`px-2 py-0.5 rounded-md font-medium transition-colors cursor-pointer ${followupFilter === 'all' ? 'bg-background text-foreground shadow-2xs font-bold' : 'text-muted-foreground hover:text-foreground'}`}
                      >
                        All ({followups.length})
                      </button>
                      <button
                        type="button"
                        onClick={() => setFollowupFilter('pending')}
                        className={`px-2 py-0.5 rounded-md font-medium transition-colors cursor-pointer ${followupFilter === 'pending' ? 'bg-background text-foreground shadow-2xs font-bold' : 'text-muted-foreground hover:text-foreground'}`}
                      >
                        Pending ({followups.filter((f) => f.status === 'Pending').length})
                      </button>
                      <button
                        type="button"
                        onClick={() => setFollowupFilter('completed')}
                        className={`px-2 py-0.5 rounded-md font-medium transition-colors cursor-pointer ${followupFilter === 'completed' ? 'bg-background text-foreground shadow-2xs font-bold' : 'text-muted-foreground hover:text-foreground'}`}
                      >
                        Completed
                      </button>
                    </div>

                    <Button
                      type="button"
                      size="sm"
                      variant={isAddingFollowup ? 'secondary' : 'default'}
                      onClick={() => setIsAddingFollowup(!isAddingFollowup)}
                      className="h-7 text-xs gap-1 shadow-2xs cursor-pointer"
                    >
                      <Clock className="h-3 w-3" />
                      <span>{isAddingFollowup ? 'Close Form' : 'New Follow-up'}</span>
                    </Button>
                  </div>
                </div>

                {/* Schedule Followup Form */}
                {isAddingFollowup && (
                  <form onSubmit={handleSaveFollowup} className="p-3.5 rounded-xl border border-amber-300/80 bg-amber-50/30 dark:bg-amber-950/20 dark:border-amber-800 space-y-3 animate-in fade-in duration-200">
                    <div className="flex items-center justify-between pb-1 border-b border-amber-200/60 dark:border-amber-900/60">
                      <span className="text-xs font-bold text-amber-800 dark:text-amber-300 flex items-center gap-1.5">
                        <Clock className="h-3.5 w-3.5" /> Schedule Next Outreach Step
                      </span>
                      <span className="text-[10px] text-muted-foreground">Sets notification and task deadline</span>
                    </div>

                    <div>
                      <label className="text-[10px] uppercase font-bold text-muted-foreground block mb-1">Follow-up Action / Title</label>
                      <Input
                        autoFocus
                        value={newFollowupTitle}
                        onChange={(e) => setNewFollowupTitle(e.target.value)}
                        placeholder="e.g. Call to review revised pricing proposal..."
                        className="h-8 text-xs bg-background"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                      <div>
                        <label className="text-[10px] uppercase font-bold text-muted-foreground block mb-1">Channel / Type</label>
                        <select
                          value={newFollowupType}
                          onChange={(e: any) => setNewFollowupType(e.target.value)}
                          className="w-full h-8 text-xs rounded-lg border border-border bg-background px-2 focus:outline-none focus:border-primary"
                        >
                          <option value="Call">Phone Call</option>
                          <option value="Email">Email Message</option>
                          <option value="Meeting">Meeting / Catch-up</option>
                          <option value="Demo">Product Demo</option>
                          <option value="Proposal">Send Proposal</option>
                        </select>
                      </div>

                      <div>
                        <label className="text-[10px] uppercase font-bold text-muted-foreground block mb-1">Due</label>
                        <Input
                          type="datetime-local"
                          value={newFollowupDueAt}
                          onChange={(e) => setNewFollowupDueAt(e.target.value)}
                          className="h-8 text-xs bg-background"
                        />
                      </div>

                      <div>
                        <label className="text-[10px] uppercase font-bold text-muted-foreground block mb-1">Assigned Rep</label>
                        <select
                          value={newFollowupRepId}
                          onChange={(e) => setNewFollowupRepId(e.target.value)}
                          className="w-full h-8 text-xs rounded-lg border border-border bg-background px-2 focus:outline-none focus:border-primary"
                        >
                          {assignableUsers.length === 0 && <option value="">Loading...</option>}
                          {assignableUsers.map((u) => (
                            <option key={u.id} value={u.id}>
                              {u.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="text-[10px] uppercase font-bold text-muted-foreground block mb-1">Context Notes & Specific Questions to Ask</label>
                      <Input
                        value={newFollowupNotes}
                        onChange={(e) => setNewFollowupNotes(e.target.value)}
                        placeholder="e.g. Check if legal approval passed on security annexure..."
                        className="h-8 text-xs bg-background"
                      />
                    </div>

                    <div className="flex items-center justify-end gap-2 pt-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs cursor-pointer"
                        onClick={() => setIsAddingFollowup(false)}
                      >
                        Cancel
                      </Button>
                      <Button type="submit" size="sm" disabled={isSavingFollowup} className="h-7 text-xs bg-amber-600 hover:bg-amber-700 text-white cursor-pointer">
                        {isSavingFollowup ? 'Saving...' : 'Schedule Follow-up'}
                      </Button>
                    </div>
                  </form>
                )}

                {/* Edit Followup Form */}
                {editingFollowupId && (
                  <form onSubmit={handleUpdateFollowup} className="p-3.5 rounded-xl border border-amber-300/80 bg-amber-50/30 dark:bg-amber-950/20 dark:border-amber-800 space-y-3 animate-in fade-in duration-200">
                    <div className="flex items-center justify-between pb-1 border-b border-amber-200/60 dark:border-amber-900/60">
                      <span className="text-xs font-bold text-amber-800 dark:text-amber-300 flex items-center gap-1.5">
                        <Pencil className="h-3.5 w-3.5" /> Edit Follow-up Step
                      </span>
                      <button type="button" onClick={handleCancelEditFollowup} className="text-[10px] text-muted-foreground hover:text-foreground cursor-pointer">
                        Cancel
                      </button>
                    </div>

                    <div>
                      <label className="text-[10px] uppercase font-bold text-muted-foreground block mb-1">Follow-up Action / Title</label>
                      <Input
                        autoFocus
                        value={newFollowupTitle}
                        onChange={(e) => setNewFollowupTitle(e.target.value)}
                        placeholder="e.g. Call to review revised pricing proposal..."
                        className="h-8 text-xs bg-background"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-2.5">
                      <div>
                        <label className="text-[10px] uppercase font-bold text-muted-foreground block mb-1">Channel / Type</label>
                        <select
                          value={newFollowupType}
                          onChange={(e: any) => setNewFollowupType(e.target.value)}
                          className="w-full h-8 text-xs rounded-lg border border-border bg-background px-2 focus:outline-none focus:border-primary"
                        >
                          <option value="Call">Phone Call</option>
                          <option value="Email">Email Message</option>
                          <option value="Meeting">Meeting / Catch-up</option>
                          <option value="Demo">Product Demo</option>
                          <option value="Proposal">Send Proposal</option>
                        </select>
                      </div>

                      <div>
                        <label className="text-[10px] uppercase font-bold text-muted-foreground block mb-1">Due</label>
                        <Input
                          type="datetime-local"
                          value={newFollowupDueAt}
                          onChange={(e) => setNewFollowupDueAt(e.target.value)}
                          className="h-8 text-xs bg-background"
                        />
                      </div>

                      <div>
                        <label className="text-[10px] uppercase font-bold text-muted-foreground block mb-1">Priority</label>
                        <select
                          value={newFollowupPriority}
                          onChange={(e: any) => setNewFollowupPriority(e.target.value)}
                          className="w-full h-8 text-xs rounded-lg border border-border bg-background px-2 focus:outline-none focus:border-primary"
                        >
                          <option value="High">High</option>
                          <option value="Normal">Normal</option>
                          <option value="Low">Low</option>
                        </select>
                      </div>

                      <div>
                        <label className="text-[10px] uppercase font-bold text-muted-foreground block mb-1">Assigned Rep</label>
                        <select
                          value={newFollowupRepId}
                          onChange={(e) => setNewFollowupRepId(e.target.value)}
                          className="w-full h-8 text-xs rounded-lg border border-border bg-background px-2 focus:outline-none focus:border-primary"
                        >
                          {assignableUsers.length === 0 && <option value="">Loading...</option>}
                          {assignableUsers.map((u) => (
                            <option key={u.id} value={u.id}>
                              {u.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="text-[10px] uppercase font-bold text-muted-foreground block mb-1">Context Notes & Specific Questions</label>
                      <Input
                        value={newFollowupNotes}
                        onChange={(e) => setNewFollowupNotes(e.target.value)}
                        placeholder="e.g. Check if legal approval passed on security annexure..."
                        className="h-8 text-xs bg-background"
                      />
                    </div>

                    <div className="flex items-center justify-end gap-2 pt-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs cursor-pointer"
                        onClick={handleCancelEditFollowup}
                      >
                        Cancel
                      </Button>
                      <Button type="submit" size="sm" disabled={isSavingFollowupEdit} className="h-7 text-xs bg-amber-600 hover:bg-amber-700 text-white cursor-pointer">
                        {isSavingFollowupEdit ? 'Updating...' : 'Update Follow-up'}
                      </Button>
                    </div>
                  </form>
                )}

                {/* Followups List */}
                <div className="space-y-1.5">
                  {followups
                    .filter((f) => {
                      if (followupFilter === 'pending') return f.status === 'Pending'
                      if (followupFilter === 'completed') return f.status === 'Completed'
                      return true
                    })
                    .map((flw) => {
                      const isDone = flw.status === 'Completed'
                      return (
                        <div
                          key={flw.id}
                          className={`p-2.5 rounded-xl border transition-all flex items-center justify-between gap-3 group ${
                            isDone
                              ? 'bg-muted/20 border-border/40 opacity-70'
                              : 'bg-card border-border/80 hover:border-amber-400 hover:shadow-2xs'
                          }`}
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <button
                              type="button"
                              onClick={() => handleToggleFollowup(flw.id)}
                              className="text-muted-foreground hover:text-emerald-600 transition-colors shrink-0 cursor-pointer"
                            >
                              {isDone ? (
                                <CheckCircle2 className="h-4 w-4 text-emerald-500 fill-emerald-500/20" />
                              ) : (
                                <Square className="h-4 w-4" />
                              )}
                            </button>

                            <div className="min-w-0">
                              <p className={`text-xs font-semibold ${isDone ? 'line-through text-muted-foreground' : 'text-foreground'}`}>
                                {flw.title}
                              </p>
                              <div className="flex flex-wrap items-center gap-2 mt-0.5 text-[10px] text-muted-foreground">
                                <span className="font-medium text-foreground">{flw.type}</span>
                                <span>• Assigned: {flw.rep?.name || 'Unknown'}</span>
                                {flw.notes && <span>• {flw.notes}</span>}
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center gap-2 shrink-0">
                            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-md border ${
                              isDone
                                ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300'
                                : new Date(flw.dueAt) < new Date()
                                ? 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/40 dark:text-rose-300'
                                : 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300'
                            }`}>
                              {formatIndianDateTime(flw.dueAt)}
                            </span>

                            <button
                              type="button"
                              onClick={() => handleStartEditFollowup(flw)}
                              className="opacity-0 group-hover:opacity-100 p-1 text-muted-foreground hover:text-primary transition-opacity cursor-pointer"
                              title="Edit Follow-up"
                            >
                              <Pencil className="h-3 w-3" />
                            </button>

                            <button
                              type="button"
                              onClick={() => handleDeleteFollowup(flw.id)}
                              className="opacity-0 group-hover:opacity-100 p-1 text-muted-foreground hover:text-destructive transition-opacity cursor-pointer"
                              title="Delete Follow-up"
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          </div>
                        </div>
                      )
                    })}
                </div>
              </div>
            )}

            {/* 3. Call History Section */}
            {(activeTab === 'callHistory' || activeTab === 'all') && visibleSections.callHistory && (
              <div className="space-y-3 pt-3 border-t border-border/60">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="p-1.5 rounded-lg bg-blue-500/10 text-blue-600 dark:text-blue-400">
                      <History className="h-4 w-4" />
                    </span>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-bold text-foreground">Call History</h3>
                        <Badge variant="outline" className="text-[10px] font-mono font-bold bg-blue-50 text-blue-700 border-blue-300 dark:bg-blue-950/40 dark:text-blue-300">
                          {callHistory.length} interactions
                        </Badge>
                      </div>
                      <p className="text-[11px] text-muted-foreground">
                        Detailed conversation records, sentiment analysis, key meeting takeaways
                      </p>
                    </div>
                  </div>

                  <Button
                    type="button"
                    size="sm"
                    variant={isAddingHistory ? 'secondary' : 'default'}
                    onClick={() => setIsAddingHistory(!isAddingHistory)}
                    className="h-7 text-xs gap-1 shadow-2xs cursor-pointer"
                  >
                    <History className="h-3 w-3" />
                    <span>{isAddingHistory ? 'Close Form' : 'Add History Record'}</span>
                  </Button>
                </div>

                {/* Add History Record Form */}
                {isAddingHistory && (
                  <form onSubmit={handleSaveHistory} className="p-3.5 rounded-xl border border-blue-300/80 bg-blue-50/30 dark:bg-blue-950/20 dark:border-blue-800 space-y-3 animate-in fade-in duration-200">
                    <div className="flex items-center justify-between pb-1 border-b border-blue-200/60 dark:border-blue-900/60">
                      <span className="text-xs font-bold text-blue-800 dark:text-blue-300 flex items-center gap-1.5">
                        <History className="h-3.5 w-3.5" /> Document Detailed Meeting / Call Outcome
                      </span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                      <div className="sm:col-span-2">
                        <label className="text-[10px] uppercase font-bold text-muted-foreground block mb-1">Meeting / Call Topic</label>
                        <Input
                          autoFocus
                          value={newHistTitle}
                          onChange={(e) => setNewHistTitle(e.target.value)}
                          placeholder="e.g. Architecture Deep-dive & Pricing Alignment"
                          className="h-8 text-xs bg-background"
                        />
                      </div>

                      <div>
                        <label className="text-[10px] uppercase font-bold text-muted-foreground block mb-1">Rep Conducting</label>
                        <select
                          value={newHistRepId}
                          onChange={(e) => setNewHistRepId(e.target.value)}
                          className="w-full h-8 text-xs rounded-lg border border-border bg-background px-2 focus:outline-none focus:border-primary"
                        >
                          {assignableUsers.length === 0 && <option value="">Loading...</option>}
                          {assignableUsers.map((u) => (
                            <option key={u.id} value={u.id}>
                              {u.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                      <div>
                        <label className="text-[10px] uppercase font-bold text-muted-foreground block mb-1">Duration (minutes)</label>
                        <Input
                          type="number"
                          min={0}
                          value={newHistDurationMinutes}
                          onChange={(e) => setNewHistDurationMinutes(Number(e.target.value))}
                          className="h-8 text-xs bg-background"
                        />
                      </div>

                      <div>
                        <label className="text-[10px] uppercase font-bold text-muted-foreground block mb-1">Outcome Status</label>
                        <Input
                          value={newHistOutcome}
                          onChange={(e) => setNewHistOutcome(e.target.value)}
                          placeholder="Qualified & Progressed"
                          className="h-8 text-xs bg-background"
                        />
                      </div>

                      <div>
                        <label className="text-[10px] uppercase font-bold text-muted-foreground block mb-1">Client Sentiment</label>
                        <select
                          value={newHistSentiment}
                          onChange={(e: any) => setNewHistSentiment(e.target.value)}
                          className="w-full h-8 text-xs rounded-lg border border-border bg-background px-2 focus:outline-none focus:border-primary"
                        >
                          <option value="Positive">Positive / Excited</option>
                          <option value="Neutral">Neutral / Objective</option>
                          <option value="Hesitant">Hesitant / Competitor comparison</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="text-[10px] uppercase font-bold text-muted-foreground block mb-1">Executive Summary</label>
                      <textarea
                        rows={2}
                        value={newHistSummary}
                        onChange={(e) => setNewHistSummary(e.target.value)}
                        placeholder="Comprehensive summary of what was covered during the call..."
                        className="w-full text-xs rounded-lg border border-border bg-background p-2 focus:outline-none focus:border-primary resize-none"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                      <div>
                        <label className="text-[10px] uppercase font-bold text-muted-foreground block mb-1">Key Discussion Points (One per line)</label>
                        <textarea
                          rows={2}
                          value={newHistKeyPoint}
                          onChange={(e) => setNewHistKeyPoint(e.target.value)}
                          placeholder="Points covered (one per line)..."
                          className="w-full text-xs rounded-lg border border-border bg-background p-2 focus:outline-none focus:border-primary resize-none"
                        />
                      </div>

                      <div>
                        <label className="text-[10px] uppercase font-bold text-muted-foreground block mb-1">Agreed Next Step / Action</label>
                        <textarea
                          rows={2}
                          value={newHistNextAction}
                          onChange={(e) => setNewHistNextAction(e.target.value)}
                          placeholder="Specific action item following this call..."
                          className="w-full text-xs rounded-lg border border-border bg-background p-2 focus:outline-none focus:border-primary resize-none"
                        />
                      </div>
                    </div>

                    <div className="flex items-center justify-end gap-2 pt-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs cursor-pointer"
                        onClick={() => setIsAddingHistory(false)}
                      >
                        Cancel
                      </Button>
                      <Button type="submit" size="sm" disabled={isSavingHistory} className="h-7 text-xs bg-blue-600 hover:bg-blue-700 text-white cursor-pointer">
                        {isSavingHistory ? 'Saving...' : 'Save History Record'}
                      </Button>
                    </div>
                  </form>
                )}

                {/* Edit History Record Form */}
                {editingHistoryId && (
                  <form onSubmit={handleUpdateHistory} className="p-3.5 rounded-xl border border-blue-300/80 bg-blue-50/30 dark:bg-blue-950/20 dark:border-blue-800 space-y-3 animate-in fade-in duration-200">
                    <div className="flex items-center justify-between pb-1 border-b border-blue-200/60 dark:border-blue-900/60">
                      <span className="text-xs font-bold text-blue-800 dark:text-blue-300 flex items-center gap-1.5">
                        <Pencil className="h-3.5 w-3.5" /> Edit Meeting / Call Outcome Record
                      </span>
                      <button type="button" onClick={handleCancelEditHistory} className="text-[10px] text-muted-foreground hover:text-foreground cursor-pointer">
                        Cancel
                      </button>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                      <div className="sm:col-span-2">
                        <label className="text-[10px] uppercase font-bold text-muted-foreground block mb-1">Meeting / Call Topic</label>
                        <Input
                          autoFocus
                          value={newHistTitle}
                          onChange={(e) => setNewHistTitle(e.target.value)}
                          placeholder="e.g. Architecture Deep-dive & Pricing Alignment"
                          className="h-8 text-xs bg-background"
                        />
                      </div>

                      <div>
                        <label className="text-[10px] uppercase font-bold text-muted-foreground block mb-1">Rep Conducting</label>
                        <select
                          value={newHistRepId}
                          onChange={(e) => setNewHistRepId(e.target.value)}
                          className="w-full h-8 text-xs rounded-lg border border-border bg-background px-2 focus:outline-none focus:border-primary"
                        >
                          {assignableUsers.length === 0 && <option value="">Loading...</option>}
                          {assignableUsers.map((u) => (
                            <option key={u.id} value={u.id}>
                              {u.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                      <div>
                        <label className="text-[10px] uppercase font-bold text-muted-foreground block mb-1">Duration (minutes)</label>
                        <Input
                          type="number"
                          min={0}
                          value={newHistDurationMinutes}
                          onChange={(e) => setNewHistDurationMinutes(Number(e.target.value))}
                          className="h-8 text-xs bg-background"
                        />
                      </div>

                      <div>
                        <label className="text-[10px] uppercase font-bold text-muted-foreground block mb-1">Outcome Status</label>
                        <Input
                          value={newHistOutcome}
                          onChange={(e) => setNewHistOutcome(e.target.value)}
                          placeholder="Qualified & Progressed"
                          className="h-8 text-xs bg-background"
                        />
                      </div>

                      <div>
                        <label className="text-[10px] uppercase font-bold text-muted-foreground block mb-1">Client Sentiment</label>
                        <select
                          value={newHistSentiment}
                          onChange={(e: any) => setNewHistSentiment(e.target.value)}
                          className="w-full h-8 text-xs rounded-lg border border-border bg-background px-2 focus:outline-none focus:border-primary"
                        >
                          <option value="Positive">Positive / Excited</option>
                          <option value="Neutral">Neutral / Objective</option>
                          <option value="Hesitant">Hesitant / Competitor comparison</option>
                          <option value="Urgent">Urgent / High Priority</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="text-[10px] uppercase font-bold text-muted-foreground block mb-1">Executive Summary</label>
                      <textarea
                        rows={2}
                        value={newHistSummary}
                        onChange={(e) => setNewHistSummary(e.target.value)}
                        placeholder="Comprehensive summary of what was covered during the call..."
                        className="w-full text-xs rounded-lg border border-border bg-background p-2 focus:outline-none focus:border-primary resize-none"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                      <div>
                        <label className="text-[10px] uppercase font-bold text-muted-foreground block mb-1">Key Discussion Points (One per line)</label>
                        <textarea
                          rows={2}
                          value={newHistKeyPoint}
                          onChange={(e) => setNewHistKeyPoint(e.target.value)}
                          placeholder="Points covered (one per line)..."
                          className="w-full text-xs rounded-lg border border-border bg-background p-2 focus:outline-none focus:border-primary resize-none"
                        />
                      </div>

                      <div>
                        <label className="text-[10px] uppercase font-bold text-muted-foreground block mb-1">Agreed Next Step / Action</label>
                        <textarea
                          rows={2}
                          value={newHistNextAction}
                          onChange={(e) => setNewHistNextAction(e.target.value)}
                          placeholder="Specific action item following this call..."
                          className="w-full text-xs rounded-lg border border-border bg-background p-2 focus:outline-none focus:border-primary resize-none"
                        />
                      </div>
                    </div>

                    <div className="flex items-center justify-end gap-2 pt-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs cursor-pointer"
                        onClick={handleCancelEditHistory}
                      >
                        Cancel
                      </Button>
                      <Button type="submit" size="sm" disabled={isSavingHistoryEdit} className="h-7 text-xs bg-blue-600 hover:bg-blue-700 text-white cursor-pointer">
                        {isSavingHistoryEdit ? 'Updating...' : 'Update History Record'}
                      </Button>
                    </div>
                  </form>
                )}

                {/* History Timeline Cards */}
                <div className="space-y-3">
                  {callHistory.map((item) => (
                    <div
                      key={item.id}
                      className="p-3.5 rounded-xl border border-border/80 bg-card hover:bg-muted/30 transition-all space-y-2.5 shadow-2xs group"
                    >
                      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border/50 pb-2">
                        <div>
                          <h4 className="text-xs font-bold text-foreground">{item.title}</h4>
                          <span className="text-[10px] text-muted-foreground">{new Date(item.occurredAt).toLocaleString()} • Rep: {item.rep?.name || 'Unknown'}</span>
                        </div>

                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full border bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/40 dark:text-blue-300">
                            {item.outcome}
                          </span>
                          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full border bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300">
                            {item.sentiment}
                          </span>
                          <Badge variant="outline" className="text-[10px] font-mono border-border">
                            {item.durationMinutes} min
                          </Badge>

                          <button
                            type="button"
                            onClick={() => handleStartEditHistory(item)}
                            className="opacity-0 group-hover:opacity-100 p-1 text-muted-foreground hover:text-primary transition-opacity cursor-pointer"
                            title="Edit History Record"
                          >
                            <Pencil className="h-3 w-3" />
                          </button>

                          <button
                            type="button"
                            onClick={() => handleDeleteHistory(item.id)}
                            className="opacity-0 group-hover:opacity-100 p-1 text-muted-foreground hover:text-destructive transition-opacity cursor-pointer"
                            title="Delete History Record"
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </div>
                      </div>

                      <p className="text-xs text-muted-foreground leading-relaxed">
                        {item.summary}
                      </p>

                      {item.keyPoints && item.keyPoints.length > 0 && (
                        <div className="space-y-1 bg-muted/40 p-2.5 rounded-lg text-xs">
                          <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Key Discussion Points</p>
                          <ul className="space-y-1">
                            {item.keyPoints.map((pt, idx) => (
                              <li key={idx} className="flex items-start gap-1.5 text-foreground text-[11px]">
                                <Check className="h-3 w-3 text-emerald-600 mt-0.5 shrink-0" />
                                <span>{pt}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {item.nextAction && (
                        <div className="flex items-center gap-2 text-xs text-indigo-700 dark:text-indigo-300 bg-indigo-50/60 dark:bg-indigo-950/30 p-2 rounded-lg border border-indigo-200/60 dark:border-indigo-900/60">
                          <ArrowRight className="h-3.5 w-3.5 shrink-0 text-indigo-500" />
                          <span className="font-semibold text-[11px]">Next Action: {item.nextAction}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Call Recordings Section - the API hands the audio out to whoever
                can open this lead, and refuses the rest. */}
            {(activeTab === 'calls' || activeTab === 'callHistory' || activeTab === 'all') && visibleSections.recordings && (
              <LeadRecordingsPanel leadId={lead?.id} />
            )}

            {/* 4. Appointments Section */}
            {(activeTab === 'appointments' || activeTab === 'all') && visibleSections.appointments && (
              <div className="space-y-3 pt-3 border-t border-border/60">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="p-1.5 rounded-lg bg-purple-500/10 text-purple-600 dark:text-purple-400">
                      <CalendarDays className="h-4 w-4" />
                    </span>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-bold text-foreground">Appointments & Meetings</h3>
                        <Badge variant="outline" className="text-[10px] font-mono font-bold bg-purple-50 text-purple-700 border-purple-300 dark:bg-purple-950/40 dark:text-purple-300">
                          {appointments.length} booked
                        </Badge>
                      </div>
                      <p className="text-[11px] text-muted-foreground">
                        Scheduled video calls, platform demonstrations, and meeting links
                      </p>
                    </div>
                  </div>

                  <Button
                    type="button"
                    size="sm"
                    variant={isAddingAppt ? 'secondary' : 'default'}
                    onClick={() => setIsAddingAppt(!isAddingAppt)}
                    className="h-7 text-xs gap-1 shadow-2xs cursor-pointer"
                  >
                    <CalendarPlus className="h-3 w-3" />
                    <span>{isAddingAppt ? 'Close Form' : 'Book Appointment'}</span>
                  </Button>
                </div>

                {/* Book Appointment Form */}
                {isAddingAppt && (
                  <form onSubmit={handleSaveAppt} className="p-3.5 rounded-xl border border-purple-300/80 bg-purple-50/30 dark:bg-purple-950/20 dark:border-purple-800 space-y-3 animate-in fade-in duration-200">
                    <div className="flex items-center justify-between pb-1 border-b border-purple-200/60 dark:border-purple-900/60">
                      <span className="text-xs font-bold text-purple-800 dark:text-purple-300 flex items-center gap-1.5">
                        <CalendarDays className="h-3.5 w-3.5" /> Book Client Meeting / Video Demo
                      </span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                      <div className="sm:col-span-2">
                        <label className="text-[10px] uppercase font-bold text-muted-foreground block mb-1">Meeting Title</label>
                        <Input
                          autoFocus
                          value={newApptTitle}
                          onChange={(e) => setNewApptTitle(e.target.value)}
                          placeholder="e.g. Solution Demo & Requirements Discovery"
                          className="h-8 text-xs bg-background"
                        />
                      </div>

                      <div>
                        <label className="text-[10px] uppercase font-bold text-muted-foreground block mb-1">Meeting Platform</label>
                        <select
                          value={newApptType}
                          onChange={(e: any) => setNewApptType(e.target.value)}
                          className="w-full h-8 text-xs rounded-lg border border-border bg-background px-2 focus:outline-none focus:border-primary"
                        >
                          <option value="Google Meet">Google Meet</option>
                          <option value="Zoom">Zoom Meeting</option>
                          <option value="Phone Call">Phone Conference</option>
                          <option value="In-Person">In-Person Office</option>
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                      <div>
                        <label className="text-[10px] uppercase font-bold text-muted-foreground block mb-1">Date & Time</label>
                        <Input
                          type="datetime-local"
                          value={newApptScheduledAt}
                          onChange={(e) => setNewApptScheduledAt(e.target.value)}
                          required
                          className="h-8 text-xs bg-background"
                        />
                      </div>

                      <div>
                        <label className="text-[10px] uppercase font-bold text-muted-foreground block mb-1">Duration</label>
                        <select
                          value={newApptDurationMinutes}
                          onChange={(e) => setNewApptDurationMinutes(Number(e.target.value))}
                          className="w-full h-8 text-xs rounded-lg border border-border bg-background px-2 focus:outline-none focus:border-primary"
                        >
                          <option value={15}>15 mins</option>
                          <option value={30}>30 mins</option>
                          <option value={45}>45 mins</option>
                          <option value={60}>60 mins</option>
                        </select>
                      </div>

                      <div>
                        <label className="text-[10px] uppercase font-bold text-muted-foreground block mb-1">Host</label>
                        <select
                          value={newApptHostId}
                          onChange={(e) => setNewApptHostId(e.target.value)}
                          required
                          className="w-full h-8 text-xs rounded-lg border border-border bg-background px-2 focus:outline-none focus:border-primary"
                        >
                          {assignableUsers.length === 0 && <option value="">Loading...</option>}
                          {assignableUsers.map((u) => (
                            <option key={u.id} value={u.id}>
                              {u.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="text-[10px] uppercase font-bold text-muted-foreground block mb-1">
                        Invite Attendees ({newApptAttendeeIds.length} selected — they'll be notified with the meeting link)
                      </label>
                      <div className="flex flex-wrap gap-1.5 p-2 rounded-lg border border-border bg-background max-h-28 overflow-y-auto">
                        {allUsers.map((u) => {
                          const selected = newApptAttendeeIds.includes(u.id)
                          return (
                            <button
                              key={u.id}
                              type="button"
                              onClick={() =>
                                setNewApptAttendeeIds((prev) =>
                                  selected ? prev.filter((id) => id !== u.id) : [...prev, u.id]
                                )
                              }
                              className={`px-2 py-1 rounded-full text-[11px] font-medium border transition-colors cursor-pointer ${
                                selected
                                  ? 'bg-purple-600 text-white border-purple-600'
                                  : 'bg-muted/40 text-foreground border-border hover:bg-muted'
                              }`}
                            >
                              {u.name}{u.role === 'SUPERADMIN' ? ' (Super Admin)' : ''}
                            </button>
                          )
                        })}
                      </div>
                    </div>

                    <MeetingConflictNotice conflicts={apptConflicts} />

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                      <div>
                        <label className="text-[10px] uppercase font-bold text-muted-foreground block mb-1">Meeting URL / Link</label>
                        <Input
                          value={newApptUrl}
                          onChange={(e) => setNewApptUrl(e.target.value)}
                          placeholder="https://meet.google.com/xyz"
                          className="h-8 text-xs bg-background"
                        />
                      </div>

                      <div>
                        <label className="text-[10px] uppercase font-bold text-muted-foreground block mb-1">Meeting Agenda & Topics</label>
                        <Input
                          value={newApptAgenda}
                          onChange={(e) => setNewApptAgenda(e.target.value)}
                          placeholder="Product demo, answering technical questions..."
                          className="h-8 text-xs bg-background"
                        />
                      </div>
                    </div>

                    <div className="flex items-center justify-end gap-2 pt-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs cursor-pointer"
                        onClick={() => setIsAddingAppt(false)}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        size="sm"
                        disabled={isSavingAppt || apptConflicts.length > 0}
                        className="h-7 text-xs bg-purple-600 hover:bg-purple-700 text-white cursor-pointer disabled:cursor-not-allowed"
                      >
                        {isSavingAppt ? 'Booking...' : apptConflicts.length > 0 ? 'Resolve conflict first' : 'Book Appointment'}
                      </Button>
                    </div>
                  </form>
                )}

                {/* Edit Appointment Form */}
                {editingApptId && (
                  <form onSubmit={handleUpdateAppt} className="p-3.5 rounded-xl border border-purple-300/80 bg-purple-50/30 dark:bg-purple-950/20 dark:border-purple-800 space-y-3 animate-in fade-in duration-200">
                    <div className="flex items-center justify-between pb-1 border-b border-purple-200/60 dark:border-purple-900/60">
                      <span className="text-xs font-bold text-purple-800 dark:text-purple-300 flex items-center gap-1.5">
                        <Pencil className="h-3.5 w-3.5" /> Edit Client Meeting / Appointment
                      </span>
                      <button type="button" onClick={handleCancelEditAppt} className="text-[10px] text-muted-foreground hover:text-foreground cursor-pointer">
                        Cancel
                      </button>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                      <div className="sm:col-span-2">
                        <label className="text-[10px] uppercase font-bold text-muted-foreground block mb-1">Meeting Title</label>
                        <Input
                          autoFocus
                          value={newApptTitle}
                          onChange={(e) => setNewApptTitle(e.target.value)}
                          placeholder="e.g. Solution Demo & Requirements Discovery"
                          className="h-8 text-xs bg-background"
                        />
                      </div>

                      <div>
                        <label className="text-[10px] uppercase font-bold text-muted-foreground block mb-1">Meeting Platform</label>
                        <select
                          value={newApptType}
                          onChange={(e: any) => setNewApptType(e.target.value)}
                          className="w-full h-8 text-xs rounded-lg border border-border bg-background px-2 focus:outline-none focus:border-primary"
                        >
                          <option value="Google Meet">Google Meet</option>
                          <option value="Zoom">Zoom Meeting</option>
                          <option value="Phone Call">Phone Conference</option>
                          <option value="In-Person">In-Person Office</option>
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-2.5">
                      <div>
                        <label className="text-[10px] uppercase font-bold text-muted-foreground block mb-1">Date & Time</label>
                        <Input
                          type="datetime-local"
                          value={newApptScheduledAt}
                          onChange={(e) => setNewApptScheduledAt(e.target.value)}
                          required
                          className="h-8 text-xs bg-background"
                        />
                      </div>

                      <div>
                        <label className="text-[10px] uppercase font-bold text-muted-foreground block mb-1">Duration</label>
                        <select
                          value={newApptDurationMinutes}
                          onChange={(e) => setNewApptDurationMinutes(Number(e.target.value))}
                          className="w-full h-8 text-xs rounded-lg border border-border bg-background px-2 focus:outline-none focus:border-primary"
                        >
                          <option value={15}>15 mins</option>
                          <option value={30}>30 mins</option>
                          <option value={45}>45 mins</option>
                          <option value={60}>60 mins</option>
                        </select>
                      </div>

                      <div>
                        <label className="text-[10px] uppercase font-bold text-muted-foreground block mb-1">Status</label>
                        <select
                          value={newApptStatus}
                          onChange={(e: any) => setNewApptStatus(e.target.value)}
                          className="w-full h-8 text-xs rounded-lg border border-border bg-background px-2 focus:outline-none focus:border-primary"
                        >
                          <option value="Confirmed">Confirmed</option>
                          <option value="Tentative">Tentative</option>
                          <option value="Completed">Completed</option>
                          <option value="Cancelled">Cancelled</option>
                        </select>
                      </div>

                      <div>
                        <label className="text-[10px] uppercase font-bold text-muted-foreground block mb-1">Host</label>
                        <select
                          value={newApptHostId}
                          onChange={(e) => setNewApptHostId(e.target.value)}
                          required
                          className="w-full h-8 text-xs rounded-lg border border-border bg-background px-2 focus:outline-none focus:border-primary"
                        >
                          {assignableUsers.length === 0 && <option value="">Loading...</option>}
                          {assignableUsers.map((u) => (
                            <option key={u.id} value={u.id}>
                              {u.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="text-[10px] uppercase font-bold text-muted-foreground block mb-1">
                        Invite Attendees ({newApptAttendeeIds.length} selected)
                      </label>
                      <div className="flex flex-wrap gap-1.5 p-2 rounded-lg border border-border bg-background max-h-28 overflow-y-auto">
                        {allUsers.map((u) => {
                          const selected = newApptAttendeeIds.includes(u.id)
                          return (
                            <button
                              key={u.id}
                              type="button"
                              onClick={() =>
                                setNewApptAttendeeIds((prev) =>
                                  selected ? prev.filter((id) => id !== u.id) : [...prev, u.id]
                                )
                              }
                              className={`px-2 py-1 rounded-full text-[11px] font-medium border transition-colors cursor-pointer ${
                                selected
                                  ? 'bg-purple-600 text-white border-purple-600'
                                  : 'bg-muted/40 text-foreground border-border hover:bg-muted'
                              }`}
                            >
                              {u.name}{u.role === 'SUPERADMIN' ? ' (Super Admin)' : ''}
                            </button>
                          )
                        })}
                      </div>
                    </div>

                    <MeetingConflictNotice conflicts={apptConflicts} />

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                      <div>
                        <label className="text-[10px] uppercase font-bold text-muted-foreground block mb-1">Meeting URL / Link</label>
                        <Input
                          value={newApptUrl}
                          onChange={(e) => setNewApptUrl(e.target.value)}
                          placeholder="https://meet.google.com/xyz"
                          className="h-8 text-xs bg-background"
                        />
                      </div>

                      <div>
                        <label className="text-[10px] uppercase font-bold text-muted-foreground block mb-1">Meeting Agenda & Topics</label>
                        <Input
                          value={newApptAgenda}
                          onChange={(e) => setNewApptAgenda(e.target.value)}
                          placeholder="Product demo, answering technical questions..."
                          className="h-8 text-xs bg-background"
                        />
                      </div>
                    </div>

                    <div className="flex items-center justify-end gap-2 pt-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs cursor-pointer"
                        onClick={handleCancelEditAppt}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        size="sm"
                        disabled={isSavingApptEdit || apptConflicts.length > 0}
                        className="h-7 text-xs bg-purple-600 hover:bg-purple-700 text-white cursor-pointer disabled:cursor-not-allowed"
                      >
                        {isSavingApptEdit ? 'Updating...' : apptConflicts.length > 0 ? 'Resolve conflict first' : 'Update Appointment'}
                      </Button>
                    </div>
                  </form>
                )}

                {/* Appointments List */}
                <div className="space-y-2.5">
                  {appointments.map((appt) => (
                    <div
                      key={appt.id}
                      className="p-3.5 rounded-xl border border-border/80 bg-card hover:bg-muted/30 transition-all flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-2xs group"
                    >
                      <div className="flex items-start gap-3">
                        <div className="p-2.5 rounded-xl bg-purple-500/10 text-purple-600 dark:text-purple-400 text-center shrink-0">
                          <CalendarDays className="h-5 w-5 mx-auto" />
                          <span className="text-[9px] font-bold block mt-0.5">{appt.durationMinutes} min</span>
                        </div>

                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="text-xs font-bold text-foreground">{appt.title}</h4>
                            <span className="text-[10px] font-semibold px-2 py-0.2 rounded-full border bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-950/40 dark:text-purple-300">
                              {appt.type}
                            </span>
                          </div>

                          <p className="text-[11px] text-muted-foreground mt-0.5">
                            {formatIndianDateTime(appt.scheduledAt)}{' '}
                            • Host: {appt.host?.name || 'Unassigned'}
                          </p>

                          {appt.attendees.length > 0 && (
                            <p className="text-[11px] text-muted-foreground mt-0.5">
                              Invited: {appt.attendees.map((a) => a.name).join(', ')}
                            </p>
                          )}

                          {appt.agenda && (
                            <p className="text-[11px] text-foreground/80 mt-1 italic">
                              "{appt.agenda}"
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-2 shrink-0 self-end sm:self-center">
                        {appt.meetingUrl && (
                          <a
                            href={appt.meetingUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-primary text-primary-foreground hover:opacity-90 flex items-center gap-1 shadow-2xs transition-all"
                          >
                            <Video className="h-3.5 w-3.5" />
                            <span>Join</span>
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        )}

                        <Badge variant="outline" className="text-[10px] font-semibold">
                          {appt.status}
                        </Badge>

                        {appt.isSuperAdminOwned && !mayDeleteAlone ? (
                          <span
                            className="flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 dark:text-emerald-300 dark:bg-emerald-950/40 dark:border-emerald-800"
                            title="Created by a Super Admin — view only"
                          >
                            <Lock className="h-3 w-3" />
                            Super Admin · view only
                          </span>
                        ) : (
                          <>
                            <button
                              type="button"
                              onClick={() => handleStartEditAppt(appt)}
                              className="opacity-0 group-hover:opacity-100 p-1 text-muted-foreground hover:text-primary transition-opacity cursor-pointer"
                              title="Edit Appointment"
                            >
                              <Pencil className="h-3 w-3" />
                            </button>

                            <button
                              type="button"
                              onClick={() => handleDeleteAppt(appt.id)}
                              className="opacity-0 group-hover:opacity-100 p-1 text-muted-foreground hover:text-destructive transition-opacity cursor-pointer"
                              title="Cancel / Delete Appointment"
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Messages / Activity Feed */}
            <div className="space-y-4 pt-4 border-t border-border/60">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-bold text-foreground">Messages & History</h3>
                <span className="text-[11px] text-muted-foreground">
                  Sorted: {commentSortOrder === 'oldest' ? 'Oldest first' : 'Newest first'}
                </span>
              </div>

              <div className="space-y-3">
                {(commentSortOrder === 'oldest' ? comments : [...comments].reverse()).map((item) => (
                  <div key={item.id} className="flex items-start gap-3 text-xs">
                    {item.avatarUrl ? (
                      <img
                        src={item.avatarUrl}
                        alt={item.author}
                        className="h-6 w-6 rounded-full object-cover shrink-0 mt-0.5"
                      />
                    ) : (
                      <div className="h-6 w-6 rounded-full bg-primary text-white flex items-center justify-center font-bold text-[10px] shrink-0 mt-0.5">
                        {item.author.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-foreground">{item.author}</span>
                        <span className="text-muted-foreground text-[11px]">
                          {item.createdAt ? formatDistanceToNow(new Date(item.createdAt), { addSuffix: true }) : ''}
                        </span>
                      </div>
                      <p className="text-muted-foreground mt-0.5">{item.text}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Reply / Comment Box */}
              <form onSubmit={handlePostComment} className="mt-4 pt-2">
                <div className="border border-border rounded-xl bg-card overflow-hidden focus-within:border-primary/60 transition-colors">
                  <textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Reply / Comment"
                    rows={3}
                    className="w-full text-xs text-foreground bg-transparent p-3 focus:outline-none resize-none placeholder:text-muted-foreground"
                  />
                  <div className="flex items-center justify-between px-3 py-2 bg-muted/20 border-t border-border/40">
                    <span className="text-[10px] text-muted-foreground">Press Enter to post</span>
                    <Button type="submit" size="sm" className="h-7 text-xs gap-1">
                      <Send className="h-3 w-3" /> Post
                    </Button>
                  </div>
                </div>
              </form>
            </div>
          </div>

          {/* RIGHT COLUMN: Sidebar Metadata & Custom Fields */}
          <div className="w-full md:w-[380px] lg:w-[420px] shrink-0 bg-muted/10 p-6 space-y-6 overflow-y-auto border-l border-border/40">
            {/* 1. Status Dropdown */}
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground shrink-0">
                <CheckCircle2 className="h-4 w-4" /> Status
              </div>
              <select
                value={status}
                onChange={(e) => {
                  setStatus(e.target.value)
                  persistChanges({ status: e.target.value })
                }}
                className="px-3 py-1.5 rounded-md text-xs font-bold uppercase tracking-wider cursor-pointer border-none shadow-sm focus:ring-2 focus:ring-primary text-white"
                style={{ backgroundColor: currentStatusObj.color }}
              >
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value} className="bg-popover text-popover-foreground">
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>


            {/* 3. Schedule: the lead's next upcoming meeting (Appointments & Meetings) */}
            <div className="flex items-center justify-between text-xs">
              <span className="flex items-center gap-2 text-muted-foreground font-medium">
                <Calendar className="h-4 w-4" /> Schedule
              </span>
              <button
                type="button"
                onClick={() => {
                  setVisibleSections((prev) => ({ ...prev, appointments: true }))
                  setActiveTab('appointments')
                }}
                className="text-right text-muted-foreground hover:text-foreground font-medium hover:underline cursor-pointer flex items-center gap-1.5"
                title={nextMeeting ? `${nextMeeting.title} - open Appointments & Meetings` : 'Open Appointments & Meetings to book one'}
              >
                <span>
                  {nextMeeting
                    ? new Date(nextMeeting.scheduledAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
                    : 'No meeting scheduled'}
                </span>
                <Calendar className="h-3 w-3 opacity-60" />
              </button>
            </div>

            {/* 4. Assigned To */}
            <div className="flex items-center justify-between text-xs">
              <span className="flex items-center gap-2 text-muted-foreground font-medium">
                <User className="h-4 w-4" /> Assigned to
              </span>
              <div className="flex items-center gap-1.5">
                <span className="h-5 w-5 rounded-full bg-teal-600 text-white text-[10px] font-bold flex items-center justify-center shrink-0">
                  {(assignee || 'A').charAt(0).toUpperCase()}
                </span>
                <select
                  value={assignee}
                  onChange={(e) => {
                    setAssignee(e.target.value)
                    persistChanges({ assignee: e.target.value })
                  }}
                  className="bg-transparent font-semibold text-foreground focus:outline-none cursor-pointer"
                >
                  {assignableUsers.map((u) => (
                    <option key={u.id} value={u.name} className="bg-popover text-popover-foreground">
                      {u.username || u.name}
                    </option>
                  ))}
                  {assignee && !assignableUsers.some((u) => u.name === assignee) && (
                    <option value={assignee} className="bg-popover text-popover-foreground">
                      {assignee}
                    </option>
                  )}
                </select>
              </div>
            </div>

            {/* Services Section */}
            <div className="pt-4 border-t border-border/60 space-y-3">
              {/* Total from Master Data */}
              {services.length > 0 && (
                <div className="flex items-center justify-between p-2.5 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800">
                  <span className="text-[11px] font-semibold text-emerald-700 dark:text-emerald-300">
                    Total (from Master Data)
                  </span>
                  <span className="text-sm font-bold text-emerald-800 dark:text-emerald-200">
                    {formatCurrency(totalFromServices)}
                  </span>
                </div>
              )}
              <ServicesPicker
                selectedServices={services}
                onChange={(newServices) => {
                  setServices(newServices)
                  persistChanges({ services: newServices })
                }}
                label="Services"
                description="Services associated with this lead. Rates and totals are fetched from master data."
                showRates={true}
                masterData={masterServices}
              />
            </div>

            {/* Prominent Industry & Call Script Section */}
            <div className="pt-4 border-t border-border/60">
              <div className="rounded-xl border border-indigo-200/80 bg-indigo-50/40 dark:bg-indigo-950/20 dark:border-indigo-900/60 p-3 space-y-2.5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5 text-xs font-bold text-indigo-900 dark:text-indigo-300">
                    <Building className="h-3.5 w-3.5 text-indigo-600 dark:text-indigo-400" />
                    <span>Industry & Call Script</span>
                  </div>
                  <Badge variant="outline" className="text-[10px] font-semibold bg-indigo-100/70 border-indigo-300 text-indigo-800 dark:bg-indigo-900/60 dark:text-indigo-300">
                    15 Playbooks
                  </Badge>
                </div>

                <div className="space-y-1.5">
                  <label className="text-[10px] uppercase font-bold text-muted-foreground block">Select Industry</label>
                  <select
                    value={selectedIndustry}
                    onChange={(e) => {
                      const val = e.target.value
                      setSelectedIndustry(val)
                      persistChanges({ industry: val })
                      if (lead?.id && !lead.id.startsWith('lead-')) {
                        api.patch(`/leads/${lead.id}/industry`, { industry: val }).catch(() => {})
                      }
                    }}
                    className="w-full text-xs font-semibold text-foreground bg-background border border-border rounded-lg px-2.5 py-1.5 focus:border-indigo-500 focus:outline-none cursor-pointer"
                  >
                    {INDUSTRY_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value} className="bg-popover text-popover-foreground">
                        {opt.label} {opt.category ? `(${opt.category})` : ''}
                      </option>
                    ))}
                  </select>
                </div>

                <Button
                  type="button"
                  size="sm"
                  onClick={() => {
                    const script = selectedIndustry ? getScriptByIndustry(selectedIndustry) : null
                    openScript(script || selectedIndustry || 'web_development', name || 'Lead')
                  }}
                  className="w-full h-8 text-xs font-semibold gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white shadow-xs cursor-pointer"
                >
                  <BookOpen className="h-3.5 w-3.5" />
                  <span>Launch Sales Script</span>
                </Button>
              </div>
            </div>

            {/* Custom fields header & quick tools */}
            {visibleSections.customFields && (
              <div className="pt-4 border-t border-border/60 space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-foreground">Custom fields</span>
                  <div className="flex items-center gap-1.5 text-muted-foreground">
                    {/* All fields toggle */}
                    <button
                      onClick={() => setShowAllFields(!showAllFields)}
                      className="flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors font-medium"
                      title="All fields"
                    >
                      <MoreHorizontal className="h-3 w-3" />
                      <span>All fields</span>
                    </button>

                    {/* Priority (Flag) Dropdown */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          className={`p-1 rounded hover:bg-muted transition-colors ${
                            PRIORITY_OPTIONS.find((p) => p.value === priority)?.color || 'text-muted-foreground'
                          } hover:text-foreground`}
                          title={`Priority: ${priority}`}
                        >
                          {(() => {
                            const currentOpt = PRIORITY_OPTIONS.find((p) => p.value === priority) || PRIORITY_OPTIONS[4]
                            const IconComponent = currentOpt.icon
                            return <IconComponent className="h-3.5 w-3.5" />
                          })()}
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-44 p-1.5">
                        <DropdownMenuLabel className="text-[10px] uppercase font-bold tracking-wider text-muted-foreground px-2 py-1">
                          Priority
                        </DropdownMenuLabel>
                        {PRIORITY_OPTIONS.map((opt) => {
                          const IconComponent = opt.icon
                          const isSelected = opt.value === priority
                          return (
                            <DropdownMenuItem
                              key={opt.value}
                              onClick={() => handlePrioritySelect(opt.value)}
                              className={`flex items-center gap-2.5 px-2 py-1.5 text-xs rounded-md cursor-pointer font-medium transition-colors ${
                                isSelected ? 'bg-muted font-bold' : ''
                              }`}
                            >
                              <IconComponent className={`h-4 w-4 ${opt.color} shrink-0`} />
                              <span className="text-foreground">{opt.label}</span>
                            </DropdownMenuItem>
                          )
                        })}
                      </DropdownMenuContent>
                    </DropdownMenu>

                    {/* Calendar quick tool: jump to the lead's meetings */}
                    <button
                      type="button"
                      onClick={() => {
                        setVisibleSections((prev) => ({ ...prev, appointments: true }))
                        setActiveTab('appointments')
                      }}
                      className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                      title={nextMeeting ? `Next meeting: ${new Date(nextMeeting.scheduledAt).toLocaleString()}` : 'Appointments & Meetings'}
                    >
                      <Calendar className="h-3.5 w-3.5" />
                    </button>

                    {/* Down Arrow / More Field Options (Link) */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                          title="Add custom field / link"
                        >
                          <ChevronDown className="h-3.5 w-3.5" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48 p-1">
                        {/* Link sub-flyout */}
                        <DropdownMenuSub>
                          <DropdownMenuSubTrigger className="flex items-center gap-2 text-xs py-2 cursor-pointer font-medium">
                            <LinkIcon className="h-4 w-4 text-muted-foreground" />
                            <span>Link</span>
                          </DropdownMenuSubTrigger>
                          <DropdownMenuSubContent className="p-3 w-64 bg-popover border border-border shadow-2xl rounded-xl space-y-3">
                            <div className="space-y-1">
                              <label className="text-xs font-semibold text-muted-foreground">URL</label>
                              <Input
                                value={newLinkUrl}
                                onChange={(e) => setNewLinkUrl(e.target.value)}
                                placeholder="https://"
                                className="h-8 text-xs bg-background"
                              />
                            </div>
                            <div className="space-y-1">
                              <label className="text-xs font-semibold text-muted-foreground">Label</label>
                              <Input
                                value={newLinkLabel}
                                onChange={(e) => setNewLinkLabel(e.target.value)}
                                placeholder="e.g. Lead Profile / Pitch Deck"
                                className="h-8 text-xs bg-background"
                              />
                            </div>
                            <Button
                              type="button"
                              size="sm"
                              className="w-full h-8 text-xs bg-blue-600 hover:bg-blue-700 text-white font-medium"
                              onClick={() => handleAddCustomLink()}
                            >
                              Add
                            </Button>
                          </DropdownMenuSubContent>
                        </DropdownMenuSub>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>

                {/* Inline Add Link Form (if triggered directly) */}
                {isAddingLink && (
                  <div className="p-3 rounded-xl border border-border bg-card shadow-lg space-y-2.5 animate-in fade-in">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-foreground flex items-center gap-1.5">
                        <LinkIcon className="h-3.5 w-3.5 text-blue-600" /> Add Link
                      </span>
                      <button
                        onClick={() => setIsAddingLink(false)}
                        className="text-muted-foreground hover:text-foreground"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <div className="space-y-1">
                      <label className="text-[11px] font-semibold text-muted-foreground">URL</label>
                      <Input
                        autoFocus
                        value={newLinkUrl}
                        onChange={(e) => setNewLinkUrl(e.target.value)}
                        placeholder="https://"
                        className="h-8 text-xs"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[11px] font-semibold text-muted-foreground">Label</label>
                      <Input
                        value={newLinkLabel}
                        onChange={(e) => setNewLinkLabel(e.target.value)}
                        placeholder="e.g. LinkedIn Profile"
                        className="h-8 text-xs"
                      />
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      className="w-full h-8 text-xs bg-blue-600 hover:bg-blue-700 text-white font-medium"
                      onClick={() => handleAddCustomLink()}
                    >
                      Add
                    </Button>
                  </div>
                )}

                {/* Priority Display Field */}
                <div className="flex items-center gap-2">
                  <Flag className="h-4 w-4 text-rose-500 shrink-0" />
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-semibold cursor-pointer border border-border/50 hover:border-border transition-colors w-full justify-between">
                        {(() => {
                          const currentOpt = PRIORITY_OPTIONS.find((p) => p.value === priority) || PRIORITY_OPTIONS[4]
                          const IconComponent = currentOpt.icon
                          return (
                            <div className="flex items-center gap-1.5">
                              <IconComponent className={`h-3.5 w-3.5 ${currentOpt.color}`} />
                              <span className="text-foreground">{currentOpt.label}</span>
                            </div>
                          )
                        })()}
                        <ChevronDown className="h-3 w-3 opacity-50" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-44 p-1.5">
                      {PRIORITY_OPTIONS.map((opt) => {
                        const IconComponent = opt.icon
                        const isSelected = opt.value === priority
                        return (
                          <DropdownMenuItem
                            key={opt.value}
                            onClick={() => handlePrioritySelect(opt.value)}
                            className={`flex items-center gap-2.5 px-2 py-1.5 text-xs rounded-md cursor-pointer font-medium ${
                              isSelected ? 'bg-muted font-bold' : ''
                            }`}
                          >
                            <IconComponent className={`h-4 w-4 ${opt.color} shrink-0`} />
                            <span className="text-foreground">{opt.label}</span>
                          </DropdownMenuItem>
                        )
                      })}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
{/* Custom Links Attached */}
                {customLinks.length > 0 && (
                  <div className="space-y-1.5 pt-1">
                    <span className="text-[11px] font-semibold text-muted-foreground flex items-center gap-1.5">
                      <LinkIcon className="h-3 w-3 text-blue-500" /> Links & Attachments
                    </span>
                    <div className="space-y-1">
                      {customLinks.map((lnk) => (
                        <div
                          key={lnk.id}
                          className="flex items-center justify-between p-1.5 rounded-lg border border-border/60 bg-muted/20 text-xs hover:border-border transition-colors group"
                        >
                          <a
                            href={lnk.url.startsWith('http') ? lnk.url : `https://${lnk.url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1.5 text-blue-600 dark:text-blue-400 hover:underline truncate max-w-[280px]"
                          >
                            <ExternalLink className="h-3 w-3 shrink-0" />
                            <span className="truncate font-medium">{lnk.label || lnk.url}</span>
                          </a>
                          <button
                            onClick={() => handleRemoveCustomLink(lnk.id)}
                            className="opacity-0 group-hover:opacity-100 p-0.5 text-muted-foreground hover:text-destructive transition-opacity"
                            title="Remove link"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Email */}
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-blue-500 shrink-0" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onBlur={() => persistChanges({ email })}
                    className="w-full text-xs font-medium text-foreground bg-transparent border-b border-transparent hover:border-border focus:border-primary focus:outline-none py-1"
                    placeholder="Email (e.g. contact@example.com)"
                  />
                </div>
{/* Phone */}
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-emerald-500 shrink-0" />
                  <input
                    type="text"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    onBlur={() => persistChanges({ phone })}
                    className="w-full text-xs font-medium text-foreground bg-transparent border-b border-transparent hover:border-border focus:border-primary focus:outline-none py-1"
                    placeholder="Phone"
                  />
                </div>

                {/* Website */}
                <div className="flex items-center gap-2">
                  <ExternalLink className="h-4 w-4 text-blue-500 shrink-0" />
                  <input
                    type="text"
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                    onBlur={() => persistChanges({ website })}
                    className="w-full text-xs font-medium text-foreground bg-transparent border-b border-transparent hover:border-border focus:border-primary focus:outline-none py-1"
                    placeholder="Website (e.g. https://example.com)"
                  />
                </div>

                {/* Google Maps. A scraped lead keeps its link inside the free-text notes, where a
                    single-line input clips it, so it gets its own row. Resolving a link for a lead
                    that has none is the Location card's job, above the todo list. */}
                {mapInfo.mapsUrl && (
                  <div className="flex items-center gap-2 min-w-0">
                    <MapPin className="h-4 w-4 text-rose-500 shrink-0" />
                    <a
                      href={mapInfo.mapsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={mapInfo.mapsUrl}
                      className="w-full truncate text-xs font-medium text-rose-600 dark:text-rose-400 hover:underline"
                    >
                      Google Maps listing
                    </a>
                  </div>
                )}

                {/* Address */}
                <div className="space-y-1">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <MapPin className="h-3.5 w-3.5" /> Address
                  </div>
                  <textarea
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    onBlur={() => persistChanges({ address })}
                    rows={2}
                    className="w-full text-xs font-medium text-foreground bg-transparent border-b border-transparent hover:border-border focus:border-primary focus:outline-none py-1 resize-none"
                    placeholder="123 Main St, City, Country"
                  />
                </div>

{/* Lead Source */}
                <div className="flex items-center gap-2">
                  <Layers className="h-4 w-4 text-indigo-500 shrink-0" />
                  <select
                    value={source}
                    onChange={(e) => {
                      setSource(e.target.value)
                      persistChanges({ source: e.target.value })
                    }}
                    className="w-full text-xs font-medium text-foreground bg-transparent border-b border-transparent hover:border-border focus:border-primary focus:outline-none py-1 cursor-pointer"
                  >
                    {SOURCE_OPTIONS.map((src) => (
                      <option key={src} value={src} className="bg-popover text-popover-foreground">
                        {src}
                      </option>
                    ))}
                  </select>
                </div>
{/* Interest Level */}
                <div className="flex items-center gap-2">
                  <Flame className="h-4 w-4 text-orange-500 shrink-0" />
                  <select
                    value={interest}
                    onChange={(e) => {
                      setInterest(e.target.value)
                      persistChanges({ interest: e.target.value })
                    }}
                    className={`w-full py-1 text-xs font-medium bg-transparent border-b border-transparent hover:border-border focus:border-primary focus:outline-none cursor-pointer ${currentInterestObj.color}`}
                  >
                    {INTEREST_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value} className="bg-popover text-popover-foreground">
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
{/* Lead Rating */}
                {(() => {
                  const scrapedInfo = parseScraperRatingAndReviews(notes || lead?.notes, rating)
                  return (
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <span className="flex items-center gap-1.5 font-medium">
                          <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-500" /> Lead Rating
                        </span>
                        {rating !== null && rating !== undefined && (
                          <span className="font-semibold text-amber-600 dark:text-amber-400 text-xs">
                            {rating} / 5
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        {[1, 2, 3, 4, 5].map((star) => {
                          const isHovered = hoverRating !== null && hoverRating >= star
                          const isFilled = hoverRating === null && rating !== null && rating >= star
                          const isHalf = hoverRating === null && rating !== null && rating >= star - 0.5 && rating < star
                          return (
                            <button
                              key={star}
                              type="button"
                              onClick={() => {
                                const newRating = rating === star ? null : star
                                setRating(newRating)
                                persistChanges({ rating: newRating })
                              }}
                              onMouseEnter={() => setHoverRating(star)}
                              onMouseLeave={() => setHoverRating(null)}
                              className="p-0.5 transition-transform hover:scale-110 focus:outline-none cursor-pointer"
                              title={`${star} star${star > 1 ? 's' : ''}`}
                            >
                              <Star
                                className={`h-4 w-4 transition-colors ${
                                  isHovered || isFilled
                                    ? 'fill-amber-400 text-amber-500'
                                    : isHalf
                                    ? 'fill-amber-300/60 text-amber-500'
                                    : 'text-muted-foreground/30 fill-transparent'
                                }`}
                              />
                            </button>
                          )
                        })}
                        {scrapedInfo.reviewCount && (
                          <span className="ml-1 text-[11px] text-muted-foreground">
                            ({scrapedInfo.reviewCount})
                          </span>
                        )}
                      </div>
                      {scrapedInfo.isFromScraper && (
                        <p className="text-[10px] text-muted-foreground/80 flex items-center gap-1">
                          <span>Imported from Google Maps lead scraper</span>
                        </p>
                      )}
                    </div>
                  )
                })()}
{/* Total */}
                <div className="flex items-center gap-2">
                  <span className="font-bold text-sm text-emerald-600 shrink-0" title="Total">₹</span>
                  <input
                    type="text"
                    value={value}
                    onChange={(e) => setValue(stripCurrencyPrefix(e.target.value))}
                    onBlur={() => persistChanges({ value })}
                    className="w-full text-xs font-bold text-emerald-600 dark:text-emerald-400 bg-transparent border-b border-transparent hover:border-border focus:border-primary focus:outline-none py-1"
                    placeholder="Total Value"
                  />
                </div>
{/* Notes */}
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-purple-500 shrink-0" />
                  <input
                    type="text"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    onBlur={() => persistChanges({ notes })}
                    className="w-full text-xs font-medium text-foreground bg-transparent border-b border-transparent hover:border-border focus:border-primary focus:outline-none py-1"
                    placeholder="Notes..."
                  />
                </div>
{/* Contact Name */}
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-sky-500 shrink-0" />
                  <input
                    type="text"
                    value={contactPerson}
                    onChange={(e) => setContactPerson(e.target.value)}
                    onBlur={() => persistChanges({ contactPerson })}
                    className="w-full text-xs font-medium text-foreground bg-transparent border-b border-transparent hover:border-border focus:border-primary focus:outline-none py-1"
                    placeholder="Contact Name"
                  />
                </div>

                {/* Add Link Field */}
                <div className="flex items-center gap-2 pt-2">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="flex items-center gap-1.5 px-2 py-1 rounded text-xs font-semibold cursor-pointer border border-border/50 hover:border-border transition-colors text-muted-foreground hover:text-foreground">
                        <Plus className="h-3.5 w-3.5" /> Add Link
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-48 p-1">
                      <DropdownMenuSub>
                        <DropdownMenuSubTrigger className="flex items-center gap-2 text-xs py-2 cursor-pointer font-medium">
                          <LinkIcon className="h-4 w-4 text-muted-foreground" />
                          <span>Link</span>
                        </DropdownMenuSubTrigger>
                        <DropdownMenuSubContent className="p-3 w-64 bg-popover border border-border shadow-2xl rounded-xl space-y-3">
                          <div className="space-y-1">
                            <label className="text-xs font-semibold text-muted-foreground">URL</label>
                            <Input
                              value={newLinkUrl}
                              onChange={(e) => setNewLinkUrl(e.target.value)}
                              placeholder="https://"
                              className="h-8 text-xs bg-background"
                            />
                          </div>
                          <div className="space-y-1">
                            <label className="text-xs font-semibold text-muted-foreground">Label</label>
                            <Input
                              value={newLinkLabel}
                              onChange={(e) => setNewLinkLabel(e.target.value)}
                              placeholder="e.g. Lead Profile / Pitch Deck"
                              className="h-8 text-xs bg-background"
                            />
                          </div>
                          <Button
                            type="button"
                            size="sm"
                            className="w-full h-8 text-xs bg-blue-600 hover:bg-blue-700 text-white font-medium"
                            onClick={() => handleAddCustomLink()}
                          >
                            Add
                          </Button>
                        </DropdownMenuSubContent>
                      </DropdownMenuSub>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            )}

            {/* CRM Section */}
            {visibleSections.crm && (
              <div className="pt-4 border-t border-border/60 space-y-4">
                <span className="text-xs font-bold text-foreground">CRM</span>

                {/* Account */}
                <div className="space-y-1">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Building className="h-3.5 w-3.5" /> Account
                  </div>
                  <input
                    type="text"
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                    onBlur={() => persistChanges({ company })}
                    className="w-full text-xs font-medium text-foreground bg-transparent border-b border-transparent hover:border-border focus:border-primary focus:outline-none py-1"
                    placeholder="TitanEstate"
                  />
                </div>

                {/* Contacts */}
                <div className="space-y-1">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <User className="h-3.5 w-3.5" /> Contacts
                  </div>
                  <div className="flex items-center gap-2 py-1">
                    <span className="p-1 rounded bg-muted text-muted-foreground">
                      <User className="h-3 w-3" />
                    </span>
                    <span className="text-xs font-medium text-foreground">{contactPerson || 'No contact person'}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      onClose()
                      navigate('/app/sales/contacts?new=true')
                    }}
                    className="flex items-center gap-1.5 text-xs text-primary font-medium hover:underline pt-1 cursor-pointer"
                  >
                    <Plus className="h-3.5 w-3.5" /> add contact
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {/* Move to Deal Configuration Dialog */}
    <Dialog open={isMoveToDealOpen} onOpenChange={setIsMoveToDealOpen}>
      <DialogContent className="max-w-md bg-card border border-border p-6 rounded-2xl shadow-2xl">
        <DialogHeader className="pb-3 border-b border-border">
          <DialogTitle className="text-lg font-bold flex items-center gap-2 text-foreground">
            <span className="p-2 rounded-xl bg-emerald-500/10 text-emerald-600">
              <BadgeDollarSign className="h-5 w-5" />
            </span>
            Move Lead to Sales Deal
          </DialogTitle>
          <p className="text-xs text-muted-foreground">
            Convert lead &quot;{name}&quot; into an active deal in the sales pipeline
          </p>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-foreground">Deal Title</label>
            <Input
              value={targetDealTitle}
              onChange={(e) => setTargetDealTitle(e.target.value)}
              placeholder="e.g. Acme Enterprise Deal"
              className="text-xs"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-foreground">Initial Pipeline Stage</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {DEAL_STAGES.map((st) => (
                <button
                  key={st.value}
                  type="button"
                  disabled={st.isWon}
                  onClick={() => {
                    if (!st.isWon) setTargetDealStage(st.value)
                  }}
                  title={st.isWon ? 'Locked: Leads cannot be converted directly into Closed Won' : undefined}
                  className={`flex items-center justify-between p-2 rounded-xl border text-xs font-medium transition-all ${
                    st.isWon
                      ? 'opacity-40 cursor-not-allowed border-dashed border-border bg-muted/20 text-muted-foreground'
                      : targetDealStage === st.value
                      ? 'border-primary bg-primary/10 text-primary font-bold shadow-xs'
                      : 'border-border bg-card text-muted-foreground hover:bg-muted/60'
                  }`}
                >
                  <span className="flex items-center gap-1.5">
                    {st.isWon ? <Lock className="h-3.5 w-3.5 text-muted-foreground" /> : <Kanban className="h-3.5 w-3.5" />}
                    <span>{st.label}</span>
                    {st.isWon && <span className="text-[9px] font-normal text-muted-foreground">(Locked)</span>}
                  </span>
                  {targetDealStage === st.value && !st.isWon && <Check className="h-3.5 w-3.5" />}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-foreground">Target Deal Value</label>
            <Input
              value={targetDealValue}
              onChange={(e) => setTargetDealValue(e.target.value)}
              placeholder="$50,000"
              className="text-xs"
            />
          </div>

          <div className="p-3 bg-muted/40 rounded-xl text-[11px] text-muted-foreground space-y-1">
            <div className="flex justify-between">
              <span>Account / Client:</span>
              <span className="font-semibold text-foreground">{company || name}</span>
            </div>
            <div className="flex justify-between">
              <span>Contact:</span>
              <span className="font-semibold text-foreground">{contactPerson || name}</span>
            </div>
            <div className="flex justify-between">
              <span>Owner:</span>
              <span className="font-semibold text-foreground">{assignee}</span>
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsMoveToDealOpen(false)}
              disabled={isConvertingDeal}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={() => handleMoveToDeal()}
              disabled={isConvertingDeal}
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold flex items-center gap-1.5 shadow-md"
            >
              <BadgeDollarSign className="h-4 w-4" />
              {isConvertingDeal ? 'Moving...' : 'Move to Deal'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {/* Create Reminder Modal */}
    <Dialog open={isReminderModalOpen} onOpenChange={setIsReminderModalOpen}>
      <DialogContent className="max-w-md bg-card border-border shadow-2xl rounded-2xl p-6">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base font-bold text-foreground">
            <Bell className="h-5 w-5 text-amber-500" /> Create Lead Reminder
          </DialogTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Set a reminder that appears directly in your GoodDay notification bell.
          </p>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-foreground">Reminder Title</label>
            <Input
              value={reminderTitle}
              onChange={(e) => setReminderTitle(e.target.value)}
              placeholder={`Check in with ${contactPerson || name}`}
              className="text-xs"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-foreground">When</label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { id: 'tomorrow_morning', label: 'Tomorrow 9:00 AM' },
                { id: 'in_2_days', label: 'In 2 Days' },
                { id: 'next_week', label: 'Next Week' },
                { id: 'custom', label: 'Custom Date & Time' },
              ].map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setReminderPreset(p.id as any)}
                  className={`p-2 rounded-xl border text-xs font-medium text-left transition-all ${
                    reminderPreset === p.id
                      ? 'border-primary bg-primary/10 text-primary font-bold shadow-xs'
                      : 'border-border bg-card text-muted-foreground hover:bg-muted/60'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            {reminderPreset === 'custom' && (
              <Input
                type="datetime-local"
                value={reminderCustomDate}
                onChange={(e) => setReminderCustomDate(e.target.value)}
                className="text-xs mt-2"
              />
            )}
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-foreground">Notes (Optional)</label>
            <Textarea
              value={reminderNotes}
              onChange={(e) => setReminderNotes(e.target.value)}
              placeholder="e.g. Check pitch deck, research notes, pricing proposal..."
              className="text-xs min-h-[70px] resize-none"
            />
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsReminderModalOpen(false)}
              disabled={isCreatingReminder}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleSaveReminder}
              disabled={isCreatingReminder || !reminderTitle.trim()}
              className="bg-amber-600 hover:bg-amber-700 text-white font-semibold flex items-center gap-1.5 shadow-md"
            >
              <Bell className="h-3.5 w-3.5" />
              {isCreatingReminder ? 'Saving...' : 'Set Reminder'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {/* Make Recurring Modal */}
    <Dialog open={isRecurringModalOpen} onOpenChange={setIsRecurringModalOpen}>
      <DialogContent className="max-w-md bg-card border-border shadow-2xl rounded-2xl p-6">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base font-bold text-foreground">
            <Repeat className="h-5 w-5 text-purple-500" /> Recurring Lead Check-in
          </DialogTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Configure periodic recurrence check-in for this lead to maintain consistent engagement.
          </p>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          <div className="flex items-center justify-between p-3 rounded-xl border border-border bg-muted/20">
            <div>
              <p className="text-xs font-bold text-foreground">Enable Recurrence</p>
              <p className="text-[11px] text-muted-foreground">Keep lead active on regular schedule</p>
            </div>
            <button
              type="button"
              onClick={() => setIsRecurring(!isRecurring)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                isRecurring ? 'bg-primary' : 'bg-muted-foreground/30'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  isRecurring ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {isRecurring && (
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-foreground">Frequency</label>
              <div className="grid grid-cols-2 gap-2">
                {['Daily', 'Weekly', 'Bi-weekly', 'Monthly'].map((freq) => (
                  <button
                    key={freq}
                    type="button"
                    onClick={() => setRecurringFrequency(freq)}
                    className={`p-2 rounded-xl border text-xs font-medium text-left transition-all ${
                      recurringFrequency === freq
                        ? 'border-purple-500 bg-purple-500/10 text-purple-700 dark:text-purple-300 font-bold shadow-xs'
                        : 'border-border bg-card text-muted-foreground hover:bg-muted/60'
                    }`}
                  >
                    {freq}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsRecurringModalOpen(false)}
              disabled={isSavingRecurrence}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={() => handleSaveRecurring(isRecurring, recurringFrequency)}
              disabled={isSavingRecurrence}
              className="bg-purple-600 hover:bg-purple-700 text-white font-semibold flex items-center gap-1.5 shadow-md"
            >
              <Repeat className="h-3.5 w-3.5" />
              {isSavingRecurrence ? 'Saving...' : 'Save Recurrence'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
      <ConfirmDialog
        open={isDeleteOpen}
        onCancel={() => setIsDeleteOpen(false)}
        onConfirm={handleDelete}
        loading={isDeleting}
        danger
        confirmLabel={mayDeleteAlone ? 'Delete permanently' : 'Send for approval'}
        title={
          mayDeleteAlone
            ? `Delete "${lead?.name || 'this lead'}"?`
            : `Ask the superadmin to delete "${lead?.name || 'this lead'}"?`
        }
        message={
          mayDeleteAlone
            ? 'This removes the lead from the database for good — its call logs, recordings, follow-ups, meetings and notes go with it. Archiving as Lost is the option to keep it.'
            : 'Nothing is deleted yet. The superadmin is asked to approve this, and only after that approval is the lead — with its call logs, recordings, follow-ups, meetings and notes — removed from the database.'
        }
      >
        {!mayDeleteAlone && (
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">
              Reason — the superadmin reads this
            </label>
            <Textarea
              rows={3}
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
              placeholder="Duplicate of another lead, wrong company, the contact asked to be removed…"
              className="text-sm"
            />
          </div>
        )}
      </ConfirmDialog>
      {isOpen && <CallScriptPanel />}
    </>
  )
}
